import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { ChevronDown, ChevronUp, Search, SlidersHorizontal, X } from 'lucide-react';
import ReviewOrderPage from './pages/ReviewOrderPage';
import { buildCheckoutScheduleDateOptions, formatCheckoutScheduleDateLabel } from './utils/checkoutScheduleDates';
import { validateCheckoutCustomerDetails } from './utils/checkoutCustomerValidation';
import { brandLogoUrl } from './branding';
import {
  currencyTypeIdFromJwt,
  getPoscraftCompanyId,
  getPoscraftLocationId,
  ORDERING_CONTEXT_KEY,
  orderingContextMatchesSite,
  setPoscraftSiteIds,
} from './poscraftConfig';
import {
  buildPoscraftStripeIntentBody,
  isBelowStripeMinimumCharge,
  normalizeCompanyPaymentTypes,
  parseStripeIntentClientSecret,
  parseStripeSettingsFromPaymentTypes,
  resolveStripeChargeAmountMajor,
} from './utils/paymentTypes';

import { getCallbackAuthorizationKey, getRuntimeConfig, waitForRuntimeConfigReady } from './runtimeConfig';

const getApiConfig = () => {
  const config = getRuntimeConfig();
  return {
    baseUrl: config.BASE_URL || 'https://connect.poscraft.co.uk',
  };
};

const getCallbackConfig = () => {
  const config = getRuntimeConfig();
  return {
    baseUrl: String(config.CALLBACK_BASE_URL || 'http://connect.poscraft.co.uk').trim(),
    apiKey: String(config.CALLBACK_AUTHORIZATION_KEY || '').trim(),
  };
};

const SQL_DATETIME_MIN_MS = Date.UTC(1753, 0, 1, 12, 0, 0);
const SQL_DATETIME_MAX_MS = Date.UTC(9999, 11, 31, 23, 59, 59, 999);

const clampToSqlDateTimeRange = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  const t = d.getTime();
  if (!Number.isFinite(t)) return new Date();
  if (t < SQL_DATETIME_MIN_MS || t > SQL_DATETIME_MAX_MS) return new Date();
  return d;
};

/** UI uses "Thu, 30 Apr, 26" — JS may parse ", 26" as year 26 AD → before 1753 → SQL overflow. */
const normalizeScheduleDateLabel = (dateStr) => {
  const s = String(dateStr || '').trim();
  const m = s.match(/^(.+),\s*(\d{1,2})\s*$/);
  if (!m) return s;
  const yy = Number(m[2]);
  if (!Number.isFinite(yy) || String(m[2]).length > 2) return s;
  const fullYear = yy >= 70 ? 1900 + yy : 2000 + yy;
  return `${m[1]}, ${fullYear}`;
};

const SCHEDULE_MONTH_ABBR = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/** Parse checkout time labels like `9:20 PM`, `09:20 PM`, `21:20` (24h), `12:00am`. */
const parseScheduleClockTo24h = (timeStr) => {
  const raw = String(timeStr || '').trim();
  if (!raw) return null;
  const withMeridian = raw.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (withMeridian) {
    let h = Number(withMeridian[1]);
    const min = Number(withMeridian[2]);
    const ap = withMeridian[3].toUpperCase();
    if (!Number.isFinite(h) || !Number.isFinite(min) || min > 59 || h > 12 || h < 1) return null;
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    if (h > 23 || h < 0) return null;
    return { h, min };
  }
  const twentyFour = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFour) {
    const h = Number(twentyFour[1]);
    const min = Number(twentyFour[2]);
    if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || h < 0 || min > 59) return null;
    return { h, min };
  }
  return null;
};

/** First local time slot boundary: e.g. 19:32 → 19:40; 19:40:00.000 → 19:40. */
const getNextLocalTenMinuteBoundary = (now = new Date()) => {
  const d = new Date(now.getTime());
  const min = d.getMinutes();
  const sec = d.getSeconds();
  const ms = d.getMilliseconds();
  const remainder = min % 10;
  const notExactBoundary = sec !== 0 || ms !== 0;
  const addMin = remainder === 0 ? (notExactBoundary ? 10 : 0) : 10 - remainder;
  d.setSeconds(0, 0);
  d.setMilliseconds(0);
  d.setMinutes(min + addMin);
  return d;
};

/**
 * Store hours by local day (0 = Sun … 6 = Sat). `null` = closed.
 * Must match {@link STORE_OPENING_HOURS_LINES} — used for ASAP / “open now” only, not slot lists.
 */
const STORE_HOURS_BY_DAY = [
  { openMinutes: 17 * 60, closeMinutes: 21 * 60 + 30 }, // Sun 5pm–9:30pm
  null, // Mon closed
  { openMinutes: 9 * 60, closeMinutes: 21 * 60 + 30 }, // Tue–Thu 9am–9:30pm
  { openMinutes: 9 * 60, closeMinutes: 21 * 60 + 30 },
  { openMinutes: 9 * 60, closeMinutes: 21 * 60 + 30 },
  { openMinutes: 9 * 60, closeMinutes: 22 * 60 + 30 }, // Fri–Sat 9am–10:30pm
  { openMinutes: 9 * 60, closeMinutes: 22 * 60 + 30 },
];

const STORE_CLOSED_ORDER_MESSAGE = "We're closed right now. Please try again during our opening times.";

const STORE_OPENING_HOURS_LINES = [
  'Monday Closed',
  'Tuesday - Thursday 9am - 9.30pm',
  'Friday - Saturday 9am - 10.30pm',
  'Sunday - 5pm - 9.30pm',
];

const formatStoreAddressFromLocation = (location) => {
  if (!location) return '';
  const parts = [
    location.addressLine1 ?? location.AddressLine1,
    location.addressLine2 ?? location.AddressLine2,
    location.countyTown ?? location.CountyTown,
    location.postCode ?? location.PostCode,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

const readStorePhoneFromLocation = (location) =>
  String(location?.contactNumber ?? location?.ContactNumber ?? '').trim();

const readLocationDisplayName = (location) =>
  String(
    location?.name ?? location?.Name ?? location?.locationName ?? location?.LocationName ?? ''
  ).trim();

/** Company + location names from Poscraft (e.g. "Brand - Branch"). */
const buildStoreDisplayName = (companyName, locationName) => {
  const company = String(companyName || '').trim();
  const location = String(locationName || '').trim();
  if (company && location && company.toLowerCase() !== location.toLowerCase()) {
    return `${company} - ${location}`;
  }
  return location || company || '';
};

const readStoreContactFromConfig = () => {
  const config = getRuntimeConfig();
  const parts = [
    config.STORE_ADDRESS_LINE1,
    config.STORE_ADDRESS_LINE2,
    config.STORE_ADDRESS_TOWN,
    config.STORE_POST_CODE,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return {
    address: parts.join(', '),
    phone: String(config.STORE_PHONE || '').trim(),
  };
};

const getStoreHoursForDay = (dayOfWeek) => STORE_HOURS_BY_DAY[dayOfWeek] ?? null;

const localMinutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();

/** Slot start must fall within opening hours (close is exclusive — last slot before closing time). */
const isWithinStoreHours = (instant) => {
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) return false;
  const hours = getStoreHoursForDay(instant.getDay());
  if (!hours) return false;
  const { openMinutes, closeMinutes } = hours;
  const minutes = localMinutesOfDay(instant);
  return minutes >= openMinutes && minutes < closeMinutes;
};

const isStoreOpenNow = (now = new Date()) => isWithinStoreHours(now);

/**
 * Comparable instant for slot ordering vs "now".
 * Prefer parsing the visible time `title` on the chosen local calendar day — many APIs send a misleading `dateTime`
 * (e.g. midnight for every row).
 */
const getSlotLocalInstant = (slot, calendarDate = new Date()) => {
  const titleRaw = slot?.title ?? slot?.Title ?? slot?.time ?? slot?.Time ?? slot?.name ?? slot?.Name;
  const title = String(titleRaw ?? '').trim();
  const clockFromTitle = parseScheduleClockTo24h(title);
  if (clockFromTitle) {
    return new Date(
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      calendarDate.getDate(),
      clockFromTitle.h,
      clockFromTitle.min,
      0,
      0
    );
  }
  const rawApi = slot?.dateTime != null ? String(slot.dateTime).trim() : '';
  if (rawApi) {
    const api = new Date(rawApi);
    if (!Number.isNaN(api.getTime())) return api;
  }
  return null;
};

const parseScheduleDateLabelToLocalDate = (dateLabel) => {
  const normalized = normalizeScheduleDateLabel(dateLabel);
  return composeLocalDateTimeFromScheduleLabel(normalized, '12:00 AM');
};

const DEFAULT_SLOT_LEAD_MINUTES = 10;

/** Drops slots before the later of: next 10-minute boundary, or now + service waiting time (today only). */
const filterSlotsFromLeadTime = (slots, now = new Date(), calendarDate, waitingTimeMinutes = DEFAULT_SLOT_LEAD_MINUTES) => {
  if (!Array.isArray(slots) || !slots.length) return [];
  const cal = calendarDate || now;
  if (!sameLocalCalendarDay(cal, now)) return slots;
  const boundary = getNextLocalTenMinuteBoundary(now);
  const leadMs = Math.max(0, Number(waitingTimeMinutes) || DEFAULT_SLOT_LEAD_MINUTES) * 60 * 1000;
  const leadMin = new Date(now.getTime() + leadMs);
  const threshold = new Date(Math.max(boundary.getTime(), leadMin.getTime()));
  return slots.filter((s) => {
    const instant = getSlotLocalInstant(s, cal);
    if (!instant) return false;
    return instant.getTime() >= threshold.getTime();
  });
};

/** API slots minus unavailable rows; opening hours are enforced server-side. */
const normalizeAvailableTimeSlots = (slots, { now = new Date(), calendarDate, waitingTimeMinutes } = {}) => {
  if (!Array.isArray(slots) || !slots.length) return [];
  const cal = calendarDate || now;
  const available = slots.filter((s) => {
    if (s.isAvailable === false) return false;
    return getSlotLocalInstant(s, cal) != null;
  });
  return filterSlotsFromLeadTime(available, now, cal, waitingTimeMinutes);
};

/**
 * `normalizedLabel` is like `Thu, 5 May, 2026` (output of `normalizeScheduleDateLabel`).
 * Builds a local wall-clock Date (no `Date.parse` on a combined opaque string).
 */
const composeLocalDateTimeFromScheduleLabel = (normalizedLabel, timeStr) => {
  const s = String(normalizedLabel || '').trim();
  const lm = s.match(/^(\w{3}),\s*(\d{1,2})\s*(\w{3}),\s*(\d{4})$/);
  if (!lm) return null;
  const mon = SCHEDULE_MONTH_ABBR[lm[3]];
  if (mon == null) return null;
  const dom = Number(lm[2]);
  const y = Number(lm[4]);
  const clock = parseScheduleClockTo24h(timeStr);
  if (!clock || !Number.isFinite(dom) || !Number.isFinite(y) || dom < 1 || dom > 31) return null;
  const d = new Date(y, mon, dom, clock.h, clock.min, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== y || d.getMonth() !== mon || d.getDate() !== dom) return null;
  return d;
};

const sameLocalCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Scheduled delivery must be strictly after `orderNow` so the server never sees a pre-order time in the past.
 * Prefers API slot `dateTime` when it matches the same local calendar day as the UI date+time (exact instant).
 */
const resolvePreorderDeliveryAfterNow = ({
  orderNow,
  checkoutScheduleType,
  scheduledDate,
  scheduledTime,
  checkoutSlots,
}) => {
  const n = clampToSqlDateTimeRange(orderNow);
  if (checkoutScheduleType !== 'later') return n;

  const titleNeedle = String(scheduledTime || '').trim();
  const slot = checkoutSlots.find((s) => String(s.title || '').trim() === titleNeedle) || null;

  const label = normalizeScheduleDateLabel(scheduledDate);
  let fromLabel = composeLocalDateTimeFromScheduleLabel(label, scheduledTime);

  const rawApi = slot?.dateTime != null ? String(slot.dateTime).trim() : '';
  let fromApi = null;
  if (rawApi) {
    const api = new Date(rawApi);
    if (!Number.isNaN(api.getTime())) fromApi = clampToSqlDateTimeRange(api);
  }

  let candidate = null;
  if (fromLabel && fromApi && sameLocalCalendarDay(fromApi, fromLabel)) {
    candidate = fromApi;
  } else if (fromLabel) {
    candidate = fromLabel;
  } else if (fromApi) {
    candidate = fromApi;
  } else {
    candidate = n;
  }

  candidate = clampToSqlDateTimeRange(candidate);

  let guard = 0;
  while (candidate.getTime() <= n.getTime() && guard < 15) {
    const bumped = new Date(candidate.getTime());
    bumped.setDate(bumped.getDate() + 1);
    candidate = clampToSqlDateTimeRange(bumped);
    guard += 1;
  }
  if (candidate.getTime() <= n.getTime()) {
    candidate = clampToSqlDateTimeRange(new Date(n.getTime() + 60_000));
  }
  return candidate;
};

const readSelectedModifierIds = (selectedByGroup, groupId) => {
  const key = groupId != null && typeof groupId !== 'string' ? String(groupId) : groupId;
  const raw = selectedByGroup?.[key] ?? selectedByGroup?.[groupId];
  if (Array.isArray(raw)) return raw;
  if (raw == null || raw === '') return [];
  const n = Number(raw);
  return Number.isFinite(n) ? [n] : [];
};

const maxSelectionForGroup = (group) => {
  const raw = Number(group?.maxSelection);
  if (!Number.isFinite(raw)) return 1;
  if (raw <= 0) {
    const total = Array.isArray(group?.items) ? group.items.length : 1;
    return Math.max(1, total);
  }
  return Math.max(1, raw);
};

/** Two-decimal money for JSON (avoids float artifacts like 31.490000000000002). */
const roundMoney2 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
};

const normalizeApiBaseUrl = (value) => {
  const fallback = 'https://connect.poscraft.co.uk';
  const trimmed = (value || fallback).trim().replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed;
};

const getApiBaseUrl = () => normalizeApiBaseUrl(getApiConfig().baseUrl);
const getCallbackBaseUrl = () =>
  String(getCallbackConfig().baseUrl || 'http://connect.poscraft.co.uk')
    .trim()
    .replace(/\/+$/, '');
const isLocalDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const shouldUseDevProxy =
  isLocalDevHost && String(getRuntimeConfig().FORCE_DIRECT_API || '').toLowerCase() !== 'true';

const buildApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (shouldUseDevProxy) {
    return `${window.location.origin}/api${normalizedPath}`;
  }
  return `${getApiBaseUrl()}/api${normalizedPath}`;
};

const getApiHeaders = (headers = {}) => {
  const baseHeaders = {
    Accept: 'application/json',
    ...headers,
  };
  // Local dev: proxy injects Authorization from `.env` only (avoids stale runtime-config token).
  if (!shouldUseDevProxy) {
    const bearerToken = (getRuntimeConfig().BEARER_TOKEN || '').trim();
    if (bearerToken) {
      baseHeaders.Authorization = `Bearer ${bearerToken}`;
    }
  }
  return baseHeaders;
};

const apiFetch = (path, options = {}) => {
  const { headers = {}, ...rest } = options;
  return fetch(buildApiUrl(path), {
    ...rest,
    headers: getApiHeaders(headers),
  });
};

/** Local calendar date as `YYYY-MM-DD` for Slots API `dateTime` query param. */
const formatLocalDateYmd = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Poscraft: `GET /api/data/Locations/{id}/ServiceType/{serviceTypeId}/Slots?dateTime=YYYY-MM-DD`
 * (used for “Pick a time” and checkout schedule).
 */
const locationServiceTypeSlotsPath = (locationId, serviceTypeId, dateTime) => {
  const base = `/data/Locations/${Number(locationId)}/ServiceType/${Number(serviceTypeId)}/Slots`;
  const dateParam = dateTime != null ? String(dateTime).trim() : '';
  if (!dateParam) return base;
  return `${base}?dateTime=${encodeURIComponent(dateParam)}`;
};

/**
 * Reference-counted overflow lock on `html` + `body`.
 * Multiple modals (allergen notice, ordering flow, checkout) used to save/restore independently; restoring
 * while another lock was still active could leave `overflow: hidden` stuck until reload.
 */
const documentScrollLock = (() => {
  let depth = 0;
  let savedBody = '';
  let savedHtml = '';
  return {
    lock() {
      if (depth === 0) {
        savedBody = document.body.style.overflow;
        savedHtml = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }
      depth += 1;
    },
    unlock() {
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        document.body.style.overflow = savedBody;
        document.documentElement.style.overflow = savedHtml;
      }
    },
  };
})();

const callbackOrderUrl = (companyId, useLocalProxy = false) => {
  const id = Number(companyId) || 0;
  if (useLocalProxy || shouldUseDevProxy) return `/callback/${id}`;
  return `${getCallbackBaseUrl()}/callback/${id}`;
};

const resolveCallbackServiceType = ({ selectedService, checkoutServiceTypeId, orderConfig }) => {
  const name = String(selectedService?.name || '')
    .trim()
    .toLowerCase();

  // Name-based mapping is safer because some POS tenants use non-standard service IDs.
  if (/(delivery|deliver|home)/i.test(name)) return 'delivery';
  if (/(collect|collection|pickup|pick up|takeaway|take away|take over)/i.test(name)) return 'collection';

  const deliveryId = Number(orderConfig?.deliveryServiceTypeId);
  if (Number.isFinite(deliveryId) && Number(checkoutServiceTypeId) === deliveryId) return 'delivery';
  return 'collection';
};

const formatCallbackDateTime = (input) => {
  const d = clampToSqlDateTimeRange(input);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
};

/** Poscraft often returns this when Stripe *secret* on their server is wrong — not the callback Authorization header. */
const formatCallbackPlaceOrderError = (message) => {
  const text = String(message || '').trim();
  if (!/invalid api key/i.test(text)) return text;
  return (
    'Poscraft rejected the Stripe secret key configured for this restaurant (admin payment type), not your callback authorization key. ' +
    'Ask Poscraft support to update the live Stripe secret for company 1989 Credit/Debit Card to match account ' +
    `${String(getRuntimeConfig().STRIPE_CONNECT_ACCOUNT_ID || '').trim() || '(see STRIPE_CONNECT_ACCOUNT_ID)'}.`
  );
};

const parseErrorMessage = async (response, fallback) => {
  let responseText = '';
  try {
    responseText = await response.text();
  } catch (_error) {
    return fallback;
  }

  if (!responseText) return fallback;

  try {
    const parsed = JSON.parse(responseText);
    if (Array.isArray(parsed)) {
      const firstMessage = parsed[0]?.message;
      return firstMessage || fallback;
    }
    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
    if (typeof parsed?.Message === 'string' && parsed.Message.trim()) {
      return parsed.Message.trim();
    }
    if (typeof parsed?.exceptionMessage === 'string' && parsed.exceptionMessage.trim()) {
      return parsed.exceptionMessage.trim();
    }
    if (typeof parsed?.title === 'string' && parsed.title.trim() && typeof parsed?.detail === 'string') {
      const detail = parsed.detail.trim();
      return detail ? `${parsed.title.trim()}: ${detail}` : parsed.title.trim();
    }
  } catch (_error) {
    // Response is plain text; return as-is.
  }

  return responseText || fallback;
};

const SERVICE_UNAVAILABLE_MESSAGE =
  'This service is not available right now. Please try another option.';

const NO_ONLINE_ORDERING_SERVICES_MESSAGE =
  'Online ordering is not set up for this location in Poscraft. Enable "Available Online" on a service type, assign an Online Ordering Menu Level, and configure time slots.';

const onlineOrderingNotReadyMessage = (service) => {
  if (!service) return NO_ONLINE_ORDERING_SERVICES_MESSAGE;
  const availableOnline = service.availableOnline ?? service.AvailableOnline;
  if (availableOnline === false) {
    return `"${service.name || 'This service'}" is not enabled for online orders in Poscraft (turn on Available Online).`;
  }
  const menuLevelId = getOnlineOrderingMenuLevelId(service);
  if (!menuLevelId) {
    return `"${service.name || 'This service'}" has no Online Ordering Menu Level in Poscraft.`;
  }
  return '';
};

/** Backend sometimes returns 500 / .NET fault text; never surface that to customers. */
const isTechnicalStatusApiMessage = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  const needles = [
    'object reference',
    'not set to an instance',
    'nullreference',
    'system.',
    'sqlexception',
    'sqldatetime',
    'exception of type',
    'internal server',
    'value cannot be null',
    'sequence contains no',
    'objectdisposed',
    'an error occurred while',
  ];
  return needles.some((n) => lower.includes(n));
};

const serviceStatusMessageForUser = (statusResponse, statusPayload) => {
  if (!statusResponse?.ok) {
    return SERVICE_UNAVAILABLE_MESSAGE;
  }
  const ok = Boolean(statusPayload?.Success ?? statusPayload?.success);
  if (ok) return '';
  const apiMessage = String(statusPayload?.Message ?? statusPayload?.message ?? '').trim();
  if (apiMessage && !isTechnicalStatusApiMessage(apiMessage)) {
    return apiMessage;
  }
  return SERVICE_UNAVAILABLE_MESSAGE;
};

/** `YYYY-MM-DD` for `<input type="date" />` from API date strings (no timezone shift). */
const toDateInputValue = (value) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return '';
};

/** ISO instant (UTC midnight on that calendar date) for UpdateProfile from date picker `YYYY-MM-DD`. */
const fromDateInputToIso = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return `${s}T00:00:00.000Z`;
};

const GENDER_SELECT_TO_API = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
};

const normalizeGenderFromApi = (raw) => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw === 1) return 'male';
    if (raw === 2) return 'female';
    if (raw === 3) return 'other';
  }
  const g = String(raw ?? '').trim();
  if (!g) return '';
  const gl = g.toLowerCase();
  if (gl === 'male' || gl === 'm' || g === '1') return 'male';
  if (gl === 'female' || gl === 'f' || g === '2') return 'female';
  if (gl === 'other' || gl === 'o' || g === '3') return 'other';
  return '';
};

const localTodayYmd = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const isYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || '').trim());

/** Merge DOB / gender from local session when GET /Customers/{id} omits them (common API limitation). */
/** API sometimes returns the reset code in `Message` (e.g. dev); never show that as the main success text. */
const friendlyForgetPasswordSentMessage = (apiMessage) => {
  const raw = String(apiMessage || '').trim();
  if (!raw || /^\d+$/.test(raw)) {
    return 'If an account exists for this email, we have sent an email with instructions to reset your password.';
  }
  return raw;
};

const readSessionProfileExtras = (customerId) => {
  try {
    const raw = localStorage.getItem(CUSTOMER_SESSION_KEY);
    if (!raw) return { profileDob: '', profileGender: '' };
    const session = JSON.parse(raw);
    if (Number(session?.customerId) !== Number(customerId)) return { profileDob: '', profileGender: '' };
    const profileDob = isYmd(session.profileDob) ? String(session.profileDob).trim() : '';
    const g = normalizeGenderFromApi(session.profileGender);
    const profileGender = g === 'male' || g === 'female' || g === 'other' ? g : '';
    return { profileDob, profileGender };
  } catch (_error) {
    return { profileDob: '', profileGender: '' };
  }
};

const INLINE_PLACEHOLDER_IMAGE =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'><rect width='300' height='300' fill='%23f2f2f2'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23909090' font-family='Arial' font-size='20'>No Image</text></svg>";
const CUSTOMER_SESSION_KEY = 'app_menu_customer_session';
const LEGACY_ORDERING_CONTEXT_KEY = 'app_menu_ordering_context_v1';

const formatPrice = (price) => {
  if (typeof price !== 'number') return '£0.00';
  return `£${price.toFixed(2)}`;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const extractApiArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

/** Poscraft MenuItemDetail.productType — `2` = meal deal / bundle (nested modifier items). */
const PRODUCT_TYPE_MEAL_DEAL = 2;

const readDetailProductType = (detail) => {
  const n = Number(detail?.productType ?? detail?.ProductType ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** True when API marks a meal deal or any top-level modifier item carries nested `menuModifierGroups`. */
const isMealDealBundleProductDetail = (detail) => {
  if (readDetailProductType(detail) === PRODUCT_TYPE_MEAL_DEAL) return true;
  const raw = detail?.menuModifierGroups ?? detail?.MenuModifierGroups ?? [];
  for (const g of extractApiArray(raw)) {
    const items = extractApiArray(g?.modifierItems ?? g?.modifiers ?? g?.items ?? []);
    for (const it of items) {
      const nested = extractApiArray(it?.menuModifierGroups ?? it?.MenuModifierGroups ?? []);
      if (nested.some((ng) => extractApiArray(ng?.modifierItems ?? ng?.modifiers ?? []).length > 0)) return true;
    }
  }
  return false;
};

/** State key for modifiers nested under a chosen parent option (e.g. sausage → ADD CHIPS!). */
const nestedModifierStateKey = (parentGroupId, parentItemId, nestedGroupId) =>
  `${parentGroupId}:item:${parentItemId}:ng:${nestedGroupId}`;

const stripNestedModifierKeysForParentGroup = (prev, parentGroupId) => {
  const prefix = `${parentGroupId}:item:`;
  const next = { ...prev };
  let changed = false;
  Object.keys(next).forEach((k) => {
    if (k.startsWith(prefix)) {
      delete next[k];
      changed = true;
    }
  });
  return changed ? next : prev;
};

const stripNestedModifierKeysForOneParentItem = (prev, parentGroupId, parentItemId) => {
  const prefix = `${parentGroupId}:item:${parentItemId}:ng:`;
  const next = { ...prev };
  let changed = false;
  Object.keys(next).forEach((k) => {
    if (k.startsWith(prefix)) {
      delete next[k];
      changed = true;
    }
  });
  return changed ? next : prev;
};

const getSafeImageUrl = (value, baseUrl) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Avoid expensive inline payloads and malformed values.
  if (trimmed.startsWith('data:')) return null;
  try {
    return new URL(trimmed, baseUrl).toString();
  } catch (error) {
    return null;
  }
};

const getBase64ImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `data:image/png;base64,${trimmed}`;
};

const withDefaultProductImage = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = INLINE_PLACEHOLDER_IMAGE;
};

const readOrderingContext = () => {
  try {
    sessionStorage.removeItem(LEGACY_ORDERING_CONTEXT_KEY);
    const raw = sessionStorage.getItem(ORDERING_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!orderingContextMatchesSite(parsed)) {
      sessionStorage.removeItem(ORDERING_CONTEXT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeOrderingContext = (payload) => {
  try {
    sessionStorage.setItem(ORDERING_CONTEXT_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

const clearOrderingContext = () => {
  try {
    sessionStorage.removeItem(ORDERING_CONTEXT_KEY);
  } catch {
    // ignore
  }
};

const getOnlineOrderingMenuLevelId = (service) => {
  if (!service || typeof service !== 'object') return 0;
  const n = Number(
    service.OnlineOrderingMenuLevelId ?? service.onlineOrderingMenuLevelId ?? service.menuLevelId ?? 0
  );
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const normalizeMenuDetailModifierItem = (m) => {
  const mid = m?.id ?? m?.modifierItemId ?? m?.menuModifierId ?? m?.modifierId;
  if (mid == null) return null;
  const nestedRaw = m?.menuModifierGroups ?? m?.MenuModifierGroups ?? [];
  const childGroups = extractApiArray(nestedRaw)
    .map((g) => normalizeMenuDetailModifierGroup(g))
    .filter(Boolean);
  const desc = (m?.description && String(m.description).trim()) || '';
  return {
    id: Number(mid),
    name: m?.name || m?.title || `Option ${mid}`,
    price: toNumber(m?.salePrice ?? m?.price ?? m?.additionalPrice ?? m?.extraPrice ?? 0),
    active: m?.active !== false,
    imageUrl: getSafeImageUrl(m?.imageUrl, getApiConfig().baseUrl),
    description: desc || undefined,
    childGroups: childGroups.length ? childGroups : undefined,
  };
};

const normalizeMenuDetailModifierGroup = (group) => {
  const id = group?.id ?? group?.modifierGroupId ?? group?.menuModifierGroupId;
  if (id == null) return null;
  const rawItems =
    group?.modifiers ??
    group?.modifierItems ??
    group?.items ??
    group?.menuModifiers ??
    group?.Modifiers ??
    [];
  const items = extractApiArray(rawItems).map(normalizeMenuDetailModifierItem).filter(Boolean);
  if (!items.length) return null;
  const minSel = toNumber(
    group?.min ??
      group?.minSelection ??
      group?.minimumSelection ??
      group?.minimum ??
      group?.minSelections ??
      0
  );
  return {
    id: Number(id),
    name: group?.name || group?.title || group?.groupName || 'Options',
    sortOrder: toNumber(group?.sortOrder ?? group?.SortOrder ?? 0),
    minSelection: minSel,
    maxSelection: toNumber(
      group?.max ??
        group?.maxSelection ??
        group?.maximumSelection ??
        group?.maximum ??
        group?.maxSelections ??
        1
    ),
    required: Boolean(group?.required ?? group?.isRequired) || minSel > 0,
    items,
  };
};

const parseMenuItemDetailModifierGroups = (detail) => {
  const raw = detail?.menuModifierGroups ?? detail?.MenuModifierGroups ?? [];
  return extractApiArray(raw).map(normalizeMenuDetailModifierGroup).filter(Boolean);
};

const bundleSeasoningStateKey = (slotIndex, seasoningGroupId) =>
  `bundleSlot:${slotIndex}:seasoning:${seasoningGroupId}`;

const bundleSlotGroupIsRequiredLike = (g) =>
  Boolean(g?.required) || Math.max(0, Number(g?.minSelection ?? 0)) > 0;

/** Pairs “CHOOSE YOUR SAUSAGE” groups with “CHOOSE YOUR SEASONING” for multi-slot bundle UI. */
const buildBundleSlotPlan = (groups) => {
  if (!Array.isArray(groups) || groups.length < 2) return [];
  const nameOf = (g) => String(g?.name || '').toLowerCase();
  const sausages = groups
    .filter((g) => /sausage/.test(nameOf(g)) && bundleSlotGroupIsRequiredLike(g))
    .sort((a, b) => toNumber(a.sortOrder) - toNumber(b.sortOrder) || a.id - b.id);
  if (sausages.length < 2) return [];
  const seasonings = groups
    .filter((g) => /seasoning/.test(nameOf(g)) && bundleSlotGroupIsRequiredLike(g))
    .sort((a, b) => toNumber(a.sortOrder) - toNumber(b.sortOrder) || a.id - b.id);
  return sausages.map((sausageGroup, idx) => ({
    sausageGroup,
    seasoningGroup: seasonings.length ? seasonings[idx % seasonings.length] : null,
    slotIndex: idx,
  }));
};

const groupIdsInBundleSlotPlan = (plan) => {
  const ids = new Set();
  if (!Array.isArray(plan)) return ids;
  plan.forEach((slot) => {
    if (slot?.sausageGroup?.id != null) ids.add(slot.sausageGroup.id);
    if (slot?.seasoningGroup?.id != null) ids.add(slot.seasoningGroup.id);
  });
  return ids;
};

const formatMenuListItemPriceLabel = (item) => {
  const salePrice = toNumber(item?.salePrice);
  if (salePrice > 0) return formatPrice(salePrice);
  const minP = toNumber(item?.minSalePrice);
  const maxP = toNumber(item?.maxSalePrice);
  if (minP > 0 && maxP > minP) return `From ${formatPrice(minP)}`;
  if (minP > 0) return formatPrice(minP);
  if (maxP > 0) return formatPrice(maxP);
  return '£0.00';
};

const menuListItemBasePrice = (item) => {
  const salePrice = toNumber(item?.salePrice);
  if (salePrice > 0) return salePrice;
  const minP = toNumber(item?.minSalePrice);
  const maxP = toNumber(item?.maxSalePrice);
  if (minP > 0) return minP;
  if (maxP > 0) return maxP;
  return 0;
};

/** API returns e.g. `FISH`; match case-insensitively. */
const isFishCategoryTitle = (title) => String(title || '').trim().toLowerCase() === 'fish';

const isFishCodProduct = (product) => String(product?.name || '').trim().toLowerCase() === 'cod';
const isFishHaddockProduct = (product) => String(product?.name || '').trim().toLowerCase() === 'haddock';

/** Fish only: COD, then HADDOCK, then other sub-menus, then regular items. */
const orderFishCategoryProducts = (products) => {
  const bySortOrder = (a, b) => toNumber(a.sortOrder) - toNumber(b.sortOrder) || a.id - b.id;
  const cod = products.find(isFishCodProduct);
  const haddock = products.find(isFishHaddockProduct);
  const rest = products.filter((p) => !isFishCodProduct(p) && !isFishHaddockProduct(p));
  const restSubMenu = rest.filter((p) => p.subMenu).sort(bySortOrder);
  const restRegular = rest.filter((p) => !p.subMenu).sort(bySortOrder);
  return [...(cod ? [cod] : []), ...(haddock ? [haddock] : []), ...restSubMenu, ...restRegular];
};

const orderProductsForCategory = (products, categoryTitle) => {
  if (!isFishCategoryTitle(categoryTitle)) return products;
  return orderFishCategoryProducts(products);
};

const mapMenuItemToProductCard = (item, menuTypeId) => {
  const basePrice = menuListItemBasePrice(item);
  return {
    id: Number(item.id),
    name: item.name || `Item ${item.id}`,
    price: formatMenuListItemPriceLabel(item),
    basePrice,
    kcal: null,
    description: (item.description && String(item.description).trim()) || '\u00a0',
    availability: null,
    imageUrl: getSafeImageUrl(item.imageUrl, getApiConfig().baseUrl),
    base64ImageUrl: getBase64ImageUrl(item.base64ImageString),
    menuTypeId: Number(menuTypeId),
    subMenu: Boolean(item.subMenu ?? item.SubMenu),
    productType: Number(item.productType ?? item.ProductType ?? 0) || 0,
    sortOrder: toNumber(item.sortOrder ?? item.SortOrder ?? 0),
  };
};

const buildMenuSectionsFromLevelId = async (menuLevelId) => {
  const levelId = Number(menuLevelId);
  if (!Number.isFinite(levelId) || levelId <= 0) return [];

  const menuTypesResponse = await apiFetch(`/data/MenuLevels/${levelId}/MenuTypes`);
  if (!menuTypesResponse.ok) {
    throw new Error(`Menu types failed (${menuTypesResponse.status})`);
  }
  const menuTypesPayload = await menuTypesResponse.json();
  const menuTypes = extractApiArray(menuTypesPayload)
    .filter((mt) => mt.active !== false && mt.visibleOnline !== false)
    .sort((a, b) => toNumber(a.sortOrder) - toNumber(b.sortOrder));

  const sections = await Promise.all(
    menuTypes.map(async (mt) => {
      const mtId = Number(mt.id);
      const itemsResponse = await apiFetch(`/data/MenuTypes/${mtId}/MenuItems/0/0`);
      if (!itemsResponse.ok) {
        return {
          title: mt.name?.trim() || `Category ${mtId}`,
          subtitle: mt.description || undefined,
          products: [],
        };
      }
      const itemsPayload = await itemsResponse.json();
      const items = extractApiArray(itemsPayload)
        .filter((it) => it.visibleOnline !== false)
        .sort((a, b) => toNumber(a.sortOrder) - toNumber(b.sortOrder));
      const categoryTitle = mt.name?.trim() || `Category ${mtId}`;
      const products = orderProductsForCategory(
        items.map((it) => mapMenuItemToProductCard(it, mtId)),
        categoryTitle
      );
      return {
        title: categoryTitle,
        subtitle: mt.description || undefined,
        products,
      };
    })
  );

  return sections.filter((s) => s.products.length > 0);
};

const REMOVED_VOUCHER_ROUTES = ['/offers', '/purchase-gift-card', '/check-balance', '/gift-card-top-up'];

function App() {
  const PLACE_ORDER_FAILURE_MESSAGE = 'Failed to create order. Please try again later.';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAllergenNoticeOpen, setIsAllergenNoticeOpen] = useState(true);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [isPastOrdersFilterOpen, setIsPastOrdersFilterOpen] = useState(false);
  const [pastOrdersAllSales, setPastOrdersAllSales] = useState(true);
  const [pastOrdersDate, setPastOrdersDate] = useState('01/05/2026');
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signInNotice, setSignInNotice] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email');
  const [forgotResetCode, setForgotResetCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [isSendingForgotPassword, setIsSendingForgotPassword] = useState(false);
  const [isCompletingPasswordReset, setIsCompletingPasswordReset] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signUpFirstName, setSignUpFirstName] = useState('');
  const [signUpLastName, setSignUpLastName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  const [signUpPhone, setSignUpPhone] = useState('');
  const [signUpError, setSignUpError] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [currentCustomerName, setCurrentCustomerName] = useState('');
  const [sections, setSections] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [modifierGroupsById, setModifierGroupsById] = useState({});
  const [modifierLinksByProductId, setModifierLinksByProductId] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [expandedCartItemIds, setExpandedCartItemIds] = useState({});
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [isScheduleExpanded, setIsScheduleExpanded] = useState(false);
  const [checkoutScheduleType, setCheckoutScheduleType] = useState('asap');
  const [scheduledDate, setScheduledDate] = useState(() => {
    const opts = buildCheckoutScheduleDateOptions();
    return opts[0] || '';
  });
  const [scheduledTime, setScheduledTime] = useState('03:09 PM');
  const [checkoutSlots, setCheckoutSlots] = useState([]);
  const [checkoutSlotsError, setCheckoutSlotsError] = useState('');
  const [isCheckoutSlotsLoading, setIsCheckoutSlotsLoading] = useState(false);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState('cash');
  const [companyPaymentMethods, setCompanyPaymentMethods] = useState([]);
  const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(true);
  const [saveCard, setSaveCard] = useState(true);
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [resolvedCompanyName, setResolvedCompanyName] = useState('');
  const [resolvedLocationName, setResolvedLocationName] = useState('');
  const companyDisplayName = useMemo(
    () => buildStoreDisplayName(resolvedCompanyName, resolvedLocationName),
    [resolvedCompanyName, resolvedLocationName]
  );
  const [storeContact, setStoreContact] = useState(() => readStoreContactFromConfig());
  const [loggedInCustomerId, setLoggedInCustomerId] = useState(null);
  const [profileMemberNo, setProfileMemberNo] = useState('');
  const [profileWid, setProfileWid] = useState('');
  const [profileDob, setProfileDob] = useState('');
  const [profileGender, setProfileGender] = useState('');
  const [profileAddressLine1, setProfileAddressLine1] = useState('');
  const [profileAddressLine2, setProfileAddressLine2] = useState('');
  const [profileCountyTown, setProfileCountyTown] = useState('');
  const [profilePostCode, setProfilePostCode] = useState('');
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState('');
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');
  const [profileSaveSuccess, setProfileSaveSuccess] = useState('');
  const [profileReloadNonce, setProfileReloadNonce] = useState(0);
  const profileFetchKeepSuccessMessage = useRef(false);
  const paymentTypesLoadSeq = useRef(0);
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [siteConfigEpoch, setSiteConfigEpoch] = useState(0);
  const [runtimeSettings, setRuntimeSettings] = useState({
    stripePublishableKey: (getRuntimeConfig().STRIPE_PUBLISHABLE_KEY || getRuntimeConfig().stripePublishableKey || '').trim(),
    stripeConnectAccountId: String(
      getRuntimeConfig().STRIPE_CONNECT_ACCOUNT_ID || getRuntimeConfig().stripeConnectAccountId || ''
    ).trim(),
    defaultLocationId: Number(getRuntimeConfig().DEFAULT_LOCATION_ID || getPoscraftLocationId() || 1),
    collectServiceTypeId: Number(getRuntimeConfig().SERVICE_TYPE_COLLECT_ID || 1),
    deliveryServiceTypeId: Number(getRuntimeConfig().SERVICE_TYPE_DELIVERY_ID || 2),
    platformFeeAmount: (() => {
      const n = Number(getRuntimeConfig().PLATFORM_FEE_AMOUNT);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })(),
    applicationFeePercent: (() => {
      const n = Number(getRuntimeConfig().STRIPE_APPLICATION_FEE_PERCENT);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })(),
    stripeTestChargeAmount: (() => {
      const n = Number(getRuntimeConfig().STRIPE_TEST_CHARGE_AMOUNT);
      return Number.isFinite(n) && n > 0 ? n : 0;
    })(),
    useLocalCallbackProxy: false,
    currencyTypeId: (() => {
      const n = Number(getRuntimeConfig().CURRENCY_TYPE_ID);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    })(),
  });
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [placeOrderError, setPlaceOrderError] = useState('');
  const [placeOrderSuccess, setPlaceOrderSuccess] = useState('');
  const [isOrderSuccessModalOpen, setIsOrderSuccessModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [selectedModifiersByGroup, setSelectedModifiersByGroup] = useState({});
  const [modalError, setModalError] = useState('');
  const [modalDetailModifierGroups, setModalDetailModifierGroups] = useState([]);
  const [bundleFlowPhase, setBundleFlowPhase] = useState('standard');
  const [bundleSlotPlan, setBundleSlotPlan] = useState([]);
  const [bundleActiveSlotIndex, setBundleActiveSlotIndex] = useState(0);
  const [modifierAccordionOpenByGroupId, setModifierAccordionOpenByGroupId] = useState({});
  const [isProductDetailLoading, setIsProductDetailLoading] = useState(false);
  const [isServiceTypesLoading, setIsServiceTypesLoading] = useState(true);
  const [isMenuLoading, setIsMenuLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [posServiceTypes, setPosServiceTypes] = useState([]);
  const [orderingSetupStep, setOrderingSetupStep] = useState('service');
  const [isMenuBrowseOnly, setIsMenuBrowseOnly] = useState(false);
  const [selectedOrderingService, setSelectedOrderingService] = useState(null);
  const visibleCheckoutSlots = useMemo(() => {
    if (!checkoutSlots.length) return [];
    const cal = parseScheduleDateLabelToLocalDate(scheduledDate) || new Date();
    return normalizeAvailableTimeSlots(checkoutSlots, {
      now: new Date(),
      calendarDate: cal,
      waitingTimeMinutes: selectedOrderingService?.waitingTime,
    });
  }, [checkoutSlots, scheduledDate, selectedOrderingService?.waitingTime]);
  const [serviceStatusError, setServiceStatusError] = useState('');
  const [isCheckingServiceStatus, setIsCheckingServiceStatus] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [slotsLoadError, setSlotsLoadError] = useState('');
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [isSubMenuPickerOpen, setIsSubMenuPickerOpen] = useState(false);
  const [subMenuParentProduct, setSubMenuParentProduct] = useState(null);
  const [subMenuPickerItems, setSubMenuPickerItems] = useState([]);
  const [subMenuPickerError, setSubMenuPickerError] = useState('');
  const [isSubMenuPickerLoading, setIsSubMenuPickerLoading] = useState(false);
  const categoryScrollRef = useRef(null);
  const sectionRefs = useRef({});
  const categoryButtonRefs = useRef({});
  const assetBase = process.env.PUBLIC_URL || '';
  const heroStyle = {
    backgroundImage: `url(${assetBase}/assets/hero_section.jpeg)`,
  };
  const checkoutOverlayStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0.18)), url(${assetBase}/assets/hero_section.jpg)`,
  };
  const ORDER_CONFIG = useMemo(() => {
    const rawCurrency = Number(runtimeSettings.currencyTypeId);
    const fromJwt = currencyTypeIdFromJwt(getRuntimeConfig().BEARER_TOKEN);
    const currencyTypeId =
      Number.isFinite(rawCurrency) && rawCurrency > 0 ? rawCurrency : fromJwt;
    return {
      locationId: Number(runtimeSettings.defaultLocationId || getPoscraftLocationId() || 1),
      collectServiceTypeId: Number(runtimeSettings.collectServiceTypeId || 1),
      deliveryServiceTypeId: Number(runtimeSettings.deliveryServiceTypeId || 2),
      currencyTypeId,
    };
  }, [runtimeSettings]);

  const filteredSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        products: isFishCategoryTitle(section.title)
          ? orderFishCategoryProducts(section.products || [])
          : section.products || [],
      })),
    [sections]
  );

  const categories = useMemo(
    () =>
      filteredSections.map((section) => ({
        name: section.title,
        schedule: section.description ? section.description : undefined,
      })),
    [filteredSections]
  );

  const sidebarItems = useMemo(() => {
    if (isLoggedIn) {
      return [
        'PROFILE',
        'SIGN OUT',
      ];
    }

    return ['SIGN IN'];
  }, [isLoggedIn]);

  const mobileSearchSections = useMemo(() => {
    const query = mobileSearchQuery.trim().toLowerCase();
    if (!query) return [];
    return filteredSections
      .map((section) => ({
        title: section.title,
        products: (section.products || [])
          .filter((product) => String(product?.name || '').toLowerCase().includes(query))
          .slice(0, 4),
      }))
      .filter((section) => section.products.length > 0);
  }, [mobileSearchQuery, filteredSections]);

  const scrollCategories = (direction) => {
    const element = categoryScrollRef.current;
    if (!element) return;
    const amount = 220;
    element.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  const onCategoryClick = (categoryName) => {
    setActiveCategory(categoryName);
    const targetSection = sectionRefs.current[categoryName];
    if (!targetSection) return;
    targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cartSummary = useMemo(() => {
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = cartItems.reduce((sum, item) => sum + item.lineTotal, 0);
    return { totalItems, totalAmount };
  }, [cartItems]);

  const selectedPaymentMethod = useMemo(
    () => companyPaymentMethods.find((method) => method.key === checkoutPaymentMethod) || null,
    [companyPaymentMethods, checkoutPaymentMethod]
  );

  const checkoutCustomerValidation = useMemo(
    () =>
      validateCheckoutCustomerDetails({
        firstName: customerFirstName,
        lastName: customerLastName,
        phone: customerPhone,
        email: customerEmail,
      }),
    [customerFirstName, customerLastName, customerPhone, customerEmail]
  );

  const canPlaceOrder =
    cartItems.length > 0 &&
    !isPaymentMethodsLoading &&
    companyPaymentMethods.length > 0 &&
    Boolean(selectedPaymentMethod) &&
    checkoutCustomerValidation.valid;

  const activeProductModifierGroups = useMemo(() => {
    if (!selectedProduct) return [];
    if (modalDetailModifierGroups.length > 0) {
      return modalDetailModifierGroups;
    }
    const links = modifierLinksByProductId[selectedProduct.id] || [];
    return links
      .map((link) => {
        const group = modifierGroupsById[link.modifierGroupId];
        if (!group || !group.items?.length) return null;
        return {
          ...group,
          minSelection: link.minSelection ?? group.minSelection ?? 0,
          maxSelection: link.maxSelection ?? group.maxSelection ?? 1,
          required: link.required ?? group.required ?? false,
        };
      })
      .filter(Boolean);
  }, [selectedProduct, modalDetailModifierGroups, modifierLinksByProductId, modifierGroupsById]);

  const bundlePlanGroupIds = useMemo(() => groupIdsInBundleSlotPlan(bundleSlotPlan), [bundleSlotPlan]);

  const bundleRemainingModifierGroups = useMemo(() => {
    if (!bundleSlotPlan.length) return [];
    return activeProductModifierGroups.filter((g) => !bundlePlanGroupIds.has(g.id));
  }, [activeProductModifierGroups, bundleSlotPlan, bundlePlanGroupIds]);

  /** Reset when switching product/group set; accordion open by default like the reference (“open below header”). */
  const modifierAccordionLayoutKey = useMemo(() => {
    const pid = selectedProduct?.id;
    const ids = [...activeProductModifierGroups.map((g) => String(g.id))].sort().join('|');
    return `${pid ?? ''}:${ids}`;
  }, [selectedProduct?.id, activeProductModifierGroups]);

  useEffect(() => {
    if (!isProductModalOpen) {
      setModifierAccordionOpenByGroupId({});
      return;
    }
    const raw = modifierAccordionLayoutKey;
    const colon = raw.indexOf(':');
    const idsPart = colon >= 0 ? raw.slice(colon + 1) : '';
    const idList = idsPart ? idsPart.split('|').filter(Boolean) : [];
    if (!idList.length) {
      setModifierAccordionOpenByGroupId({});
      return;
    }
    const next = {};
    idList.forEach((id) => {
      next[id] = true;
    });
    setModifierAccordionOpenByGroupId(next);
  }, [isProductModalOpen, modifierAccordionLayoutKey]);

  const selectedModifierAmount = useMemo(() => {
    let sum = activeProductModifierGroups.reduce((acc, group) => {
        const selectedItemIds = readSelectedModifierIds(selectedModifiersByGroup, group.id);
      if (!selectedItemIds.length) return acc;
        return (
        acc +
          selectedItemIds.reduce((groupSum, selectedItemId) => {
            const selectedItem = group.items.find((item) => item.id === selectedItemId);
            return groupSum + (selectedItem?.price || 0);
          }, 0)
        );
    }, 0);
    activeProductModifierGroups.forEach((group) => {
      readSelectedModifierIds(selectedModifiersByGroup, group.id).forEach((parentItemId) => {
        const parentItem = group.items.find((item) => item.id === parentItemId);
        if (!parentItem?.childGroups?.length) return;
        parentItem.childGroups.forEach((cg) => {
          const sk = nestedModifierStateKey(group.id, parentItem.id, cg.id);
          readSelectedModifierIds(selectedModifiersByGroup, sk).forEach((optId) => {
            const opt = cg.items.find((o) => o.id === optId);
            sum += opt?.price || 0;
          });
        });
      });
    });
    bundleSlotPlan.forEach((slot, idx) => {
      const sg = slot.seasoningGroup;
      if (!sg) return;
      const sk = bundleSeasoningStateKey(idx, sg.id);
      readSelectedModifierIds(selectedModifiersByGroup, sk).forEach((optId) => {
        const opt = sg.items.find((o) => o.id === optId);
        sum += opt?.price || 0;
      });
    });
    return sum;
  }, [activeProductModifierGroups, selectedModifiersByGroup, bundleSlotPlan]);

  const canAddProductWithCurrentModifiers = useMemo(() => {
    if (!selectedProduct) return false;
    if (!activeProductModifierGroups.length) return true;
    const planIds = bundlePlanGroupIds;
    const topOk = !activeProductModifierGroups.some((group) => {
      if (planIds.size && planIds.has(group.id)) {
        const onlySeasoning = bundleSlotPlan.some((s) => s.seasoningGroup?.id === group.id);
        if (onlySeasoning) return false;
      }
      const selected = readSelectedModifierIds(selectedModifiersByGroup, group.id);
      const minimum = Math.max(group.required ? 1 : 0, Number(group.minSelection || 0));
      return selected.length < minimum;
    });
    if (!topOk) return false;
    for (const group of activeProductModifierGroups) {
      const parents = readSelectedModifierIds(selectedModifiersByGroup, group.id);
      for (const pId of parents) {
        const item = group.items.find((i) => i.id === pId);
        if (!item?.childGroups?.length) continue;
        for (const cg of item.childGroups) {
          const sk = nestedModifierStateKey(group.id, item.id, cg.id);
          const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
          const minimum = Math.max(cg.required ? 1 : 0, Number(cg.minSelection || 0));
          if (sel.length < minimum) return false;
        }
      }
    }
    for (let idx = 0; idx < bundleSlotPlan.length; idx++) {
      const sg = bundleSlotPlan[idx].seasoningGroup;
      if (!sg) continue;
      const sk = bundleSeasoningStateKey(idx, sg.id);
      const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
      const minimum = Math.max(sg.required ? 1 : 0, Number(sg.minSelection || 0));
      if (sel.length < minimum) return false;
    }
    return true;
  }, [
    selectedProduct,
    activeProductModifierGroups,
    selectedModifiersByGroup,
    bundleSlotPlan,
    bundlePlanGroupIds,
  ]);

  const checkoutServiceTypeId = Number(selectedOrderingService?.id || ORDER_CONFIG.collectServiceTypeId);

  const applyDefaultModifierSelections = (groups) => {
    const defaultSelections = {};
    groups.forEach((group) => {
      const min = Math.max(0, Number(group.minSelection || 0));
      const shouldPrefill = group.required || min > 0;
      if (shouldPrefill && group.items?.length) {
        const take = Math.min(Math.max(min, 1), group.items.length);
        defaultSelections[group.id] = group.items.slice(0, take).map((item) => item.id);
      }
    });
    return defaultSelections;
  };

  const loadMenuForService = async (service, slot, { browseOnly = false } = {}) => {
    const menuLevelId = getOnlineOrderingMenuLevelId(service);
    if (!menuLevelId) {
      setProductsError('This service has no online menu level configured.');
      setSections([]);
      setOrderingSetupStep('service');
      return;
    }
    setIsMenuLoading(true);
    setProductsError('');
    try {
      const built = await buildMenuSectionsFromLevelId(menuLevelId);
      if (!built.length) {
        setProductsError('No menu items were returned for this service.');
        setSections([]);
      } else {
        setSections(built);
      }
      setModifierGroupsById({});
      setModifierLinksByProductId({});
      if (!browseOnly) {
        writeOrderingContext({
          companyId: getPoscraftCompanyId(),
          locationId: ORDER_CONFIG.locationId,
          serviceTypeId: Number(service.id),
          serviceName: String(service.name || '').trim(),
          onlineOrderingMenuLevelId: menuLevelId,
          slotTitle: slot?.title || '',
          slotDateTime: slot?.dateTime || '',
        });
      }
      setOrderingSetupStep('ready');
    } catch (error) {
      console.error(error);
      setProductsError(error?.message || 'Could not load menu.');
      setSections([]);
      setOrderingSetupStep('service');
    } finally {
      setIsMenuLoading(false);
    }
  };

  const resetOrderingFlow = () => {
    clearOrderingContext();
    setIsMenuBrowseOnly(false);
    setOrderingSetupStep('service');
    setSelectedOrderingService(null);
    setSelectedSlot(null);
    setPendingSlot(null);
    setTimeSlots([]);
    setServiceStatusError('');
    setSlotsLoadError('');
    setSections([]);
    setModifierGroupsById({});
    setModifierLinksByProductId({});
    setCartItems([]);
    setProductsError('');
  };

  const handleGoBackFromSlot = () => {
    if (isSlotsLoading) return;
    setOrderingSetupStep('service');
    setSelectedOrderingService(null);
    setTimeSlots([]);
    setPendingSlot(null);
    setSlotsLoadError('');
    setServiceStatusError('');
  };

  const handleViewMenuOnly = async () => {
    const service = posServiceTypes.find((svc) => getOnlineOrderingMenuLevelId(svc) > 0);
    if (!service) {
      setServiceStatusError(NO_ONLINE_ORDERING_SERVICES_MESSAGE);
      return;
    }
    setServiceStatusError('');
    setIsMenuBrowseOnly(true);
    setSelectedOrderingService(service);
    setSelectedSlot(null);
    setCartItems([]);
    await loadMenuForService(service, null, { browseOnly: true });
  };

  const handleSelectPosService = async (service) => {
    setIsMenuBrowseOnly(false);
    const loc = ORDER_CONFIG.locationId;
    setServiceStatusError('');
    setSlotsLoadError('');
    const onlineSetupError = onlineOrderingNotReadyMessage(service);
    if (onlineSetupError) {
      setServiceStatusError(onlineSetupError);
      return;
    }
    setIsCheckingServiceStatus(true);
    try {
      const statusRes = await apiFetch(`/data/Locations/${loc}/ServiceType/${Number(service.id)}/Status`);
      const statusJson = await statusRes.json().catch(() => ({}));
      const ok = Boolean(statusJson?.Success ?? statusJson?.success);
      if (!statusRes.ok || !ok) {
        setServiceStatusError(serviceStatusMessageForUser(statusRes, statusJson));
        return;
      }
      setSelectedOrderingService(service);
      setOrderingSetupStep('slot');
      setIsSlotsLoading(true);
      setTimeSlots([]);
      setPendingSlot(null);
      const slotsRes = await apiFetch(
        locationServiceTypeSlotsPath(loc, service.id, formatLocalDateYmd(new Date()))
      );
      if (!slotsRes.ok) {
        setSlotsLoadError(`Could not load time slots (${slotsRes.status})`);
        setTimeSlots([]);
        return;
      }
      const slotsPayload = await slotsRes.json();
      const now = new Date();
      const slots = normalizeAvailableTimeSlots(extractApiArray(slotsPayload), {
        now,
        calendarDate: now,
        waitingTimeMinutes: service.waitingTime,
      });
      setTimeSlots(slots);
    } catch (_error) {
      setServiceStatusError(SERVICE_UNAVAILABLE_MESSAGE);
      setSelectedOrderingService(null);
      setOrderingSetupStep('service');
      setTimeSlots([]);
      setPendingSlot(null);
    } finally {
      setIsCheckingServiceStatus(false);
      setIsSlotsLoading(false);
    }
  };

  const handleConfirmSlot = async (slot) => {
    if (!selectedOrderingService) return;
    setSelectedSlot(slot);
    await loadMenuForService(selectedOrderingService, slot);
  };

  const skipSlotAndContinue = async () => {
    if (!selectedOrderingService) return;
    if (!isStoreOpenNow()) {
      setSlotsLoadError(STORE_CLOSED_ORDER_MESSAGE);
      return;
    }
    setSlotsLoadError('');
    await handleConfirmSlot({ title: 'ASAP', dateTime: '', isAvailable: true });
  };

  const fetchAndShowProductModal = async (product) => {
    setModalError('');
    setModalQuantity(1);
    setIsProductDetailLoading(true);
    setModalDetailModifierGroups([]);
    setSelectedProduct(product);
    try {
      const response = await apiFetch(`/data/MenuTypes/MenuItemDetail/${Number(product.id)}/0/0`);
      if (!response.ok) {
        const message = await parseErrorMessage(response, `Could not load item (${response.status})`);
        throw new Error(message);
      }
      const detail = await response.json();
      const groups = parseMenuItemDetailModifierGroups(detail);
      const sale = toNumber(detail?.salePrice);
      const bundlePlan = isMealDealBundleProductDetail(detail) ? buildBundleSlotPlan(groups) : [];
      const enriched = {
        ...product,
        name: detail?.name || product.name,
        description: (detail?.description && String(detail.description).trim()) || product.description,
        basePrice: sale > 0 ? sale : product.basePrice,
        price: sale > 0 ? formatPrice(sale) : product.price,
        imageUrl: getSafeImageUrl(detail?.imageUrl, getApiConfig().baseUrl) || product.imageUrl,
        productType: readDetailProductType(detail),
        isMealDealBundle: isMealDealBundleProductDetail(detail),
      };
      setModalDetailModifierGroups(groups);
      setBundleSlotPlan(bundlePlan);
      setBundleActiveSlotIndex(0);
      setBundleFlowPhase(bundlePlan.length ? 'overview' : 'standard');
      setSelectedProduct(enriched);
      setSelectedModifiersByGroup(bundlePlan.length ? {} : applyDefaultModifierSelections(groups));
      setIsProductModalOpen(true);
    } catch (error) {
      setModalError(error?.message || 'Could not load customization options.');
      setSelectedProduct(product);
      setModalDetailModifierGroups([]);
      setBundleSlotPlan([]);
      setBundleFlowPhase('standard');
      setBundleActiveSlotIndex(0);
      setSelectedModifiersByGroup({});
      setIsProductModalOpen(true);
    } finally {
      setIsProductDetailLoading(false);
    }
  };

  const openSubMenuPickerForProduct = async (product) => {
    setSubMenuParentProduct(product);
    setSubMenuPickerItems([]);
    setSubMenuPickerError('');
    setIsSubMenuPickerLoading(true);
    setIsSubMenuPickerOpen(true);
    try {
      const response = await apiFetch(`/data/MenuTypes/${Number(product.id)}/SubMenuItems/0/0`);
      if (!response.ok) {
        const message = await parseErrorMessage(response, `Sub-menu failed (${response.status})`);
        throw new Error(message);
      }
      const payload = await response.json();
      const list = extractApiArray(payload).filter((row) => row.active !== false);
      setSubMenuPickerItems(list);
      if (!list.length) {
        setSubMenuPickerError('No options are available for this item yet.');
      }
    } catch (error) {
      setSubMenuPickerError(error?.message || 'Could not load options.');
    } finally {
      setIsSubMenuPickerLoading(false);
    }
  };

  const closeSubMenuPicker = () => {
    setIsSubMenuPickerOpen(false);
    setSubMenuParentProduct(null);
    setSubMenuPickerItems([]);
    setSubMenuPickerError('');
    setIsSubMenuPickerLoading(false);
  };

  const openProductModal = (product) => {
    if (!product) return;
    if (product.subMenu) {
      void openSubMenuPickerForProduct(product);
      return;
    }
    void fetchAndShowProductModal(product);
  };

  const onPickSubMenuItem = (row) => {
    const parent = subMenuParentProduct;
    if (!parent) return;
    const leaf = {
      id: Number(row.id),
      name: row.name || `Item ${row.id}`,
      price: formatPrice(toNumber(row.salePrice)),
      basePrice: toNumber(row.salePrice),
      description: (row.description && String(row.description).trim()) || '\u00a0',
      availability: null,
      imageUrl: getSafeImageUrl(row.imageUrl, getApiConfig().baseUrl),
      base64ImageUrl: getBase64ImageUrl(row.base64ImageString),
      menuTypeId: parent.menuTypeId,
      subMenu: false,
    };
    closeSubMenuPicker();
    void fetchAndShowProductModal(leaf);
  };

  const closeProductModal = () => {
    setIsProductModalOpen(false);
    setSelectedProduct(null);
    setModalQuantity(1);
    setSelectedModifiersByGroup({});
    setModalError('');
    setModalDetailModifierGroups([]);
    setBundleSlotPlan([]);
    setBundleFlowPhase('standard');
    setBundleActiveSlotIndex(0);
    setModifierAccordionOpenByGroupId({});
    setIsProductDetailLoading(false);
  };

  const toggleModifierAccordion = (groupId) => {
    const key = String(groupId);
    setModifierAccordionOpenByGroupId((prev) => {
      const current = prev[key] ?? true;
      return { ...prev, [key]: !current };
    });
  };

  const onSelectModifierItem = (groupId, itemId) => {
    setSelectedModifiersByGroup((prev) => {
      const group = activeProductModifierGroups.find((g) => String(g.id) === String(groupId));
      if (!group) return prev;
      const maxSelection = maxSelectionForGroup(group);
      const current = Array.isArray(prev[groupId]) ? prev[groupId] : [];
      const has = current.includes(itemId);

      if (has) {
        setModalError('');
        let next = { ...prev, [groupId]: current.filter((id) => id !== itemId) };
        next = stripNestedModifierKeysForOneParentItem(next, groupId, itemId);
        return next;
      }

      if (maxSelection === 1) {
        setModalError('');
        let next = stripNestedModifierKeysForParentGroup(prev, groupId);
        return { ...next, [groupId]: [itemId] };
      }

      if (current.length >= maxSelection) {
        setModalError(`You can select up to ${maxSelection} option${maxSelection > 1 ? 's' : ''} for ${group.name}.`);
        return prev;
      }

      setModalError('');

      return {
        ...prev,
        [groupId]: [...current, itemId],
      };
    });
  };

  const onSelectNestedModifierItem = (parentGroupId, parentItemId, nestedGroupId, optionId) => {
    const parentGroup = activeProductModifierGroups.find((g) => String(g.id) === String(parentGroupId));
    const parentItem = parentGroup?.items?.find((i) => i.id === parentItemId);
    const nestedGroup = parentItem?.childGroups?.find((g) => String(g.id) === String(nestedGroupId));
    if (!nestedGroup) return;
    const stateKey = nestedModifierStateKey(parentGroupId, parentItemId, nestedGroupId);
    setSelectedModifiersByGroup((prev) => {
      const maxSelection = maxSelectionForGroup(nestedGroup);
      const current = Array.isArray(prev[stateKey]) ? prev[stateKey] : [];
      const has = current.includes(optionId);

      if (has) {
        setModalError('');
        return { ...prev, [stateKey]: current.filter((id) => id !== optionId) };
      }

      if (maxSelection === 1) {
        setModalError('');
        return { ...prev, [stateKey]: [optionId] };
      }

      if (current.length >= maxSelection) {
        setModalError(
          `You can select up to ${maxSelection} option${maxSelection > 1 ? 's' : ''} for ${nestedGroup.name}.`
        );
        return prev;
      }

      setModalError('');
      return { ...prev, [stateKey]: [...current, optionId] };
    });
  };

  const onSelectBundleSeasoning = (slotIndex, seasoningGroupId, optionId) => {
    const slot = bundleSlotPlan[slotIndex];
    const nestedGroup = slot?.seasoningGroup;
    if (!nestedGroup || nestedGroup.id !== seasoningGroupId) return;
    const stateKey = bundleSeasoningStateKey(slotIndex, seasoningGroupId);
    setSelectedModifiersByGroup((prev) => {
      const maxSelection = maxSelectionForGroup(nestedGroup);
      const current = Array.isArray(prev[stateKey]) ? prev[stateKey] : [];
      const has = current.includes(optionId);

      if (has) {
        setModalError('');
        return { ...prev, [stateKey]: current.filter((id) => id !== optionId) };
      }

      if (maxSelection === 1) {
        setModalError('');
        return { ...prev, [stateKey]: [optionId] };
      }

      if (current.length >= maxSelection) {
        setModalError(
          `You can select up to ${maxSelection} option${maxSelection > 1 ? 's' : ''} for ${nestedGroup.name}.`
        );
        return prev;
      }

      setModalError('');
      return { ...prev, [stateKey]: [...current, optionId] };
    });
  };

  const addToCart = () => {
    if (!selectedProduct) return;
    if (isMenuBrowseOnly) {
      setModalError('Choose a service and collection time above to place an order.');
      return;
    }
    const planSeasoningIds = new Set(
      bundleSlotPlan.map((s) => s.seasoningGroup?.id).filter((id) => id != null)
    );
    const missingRequiredGroup = activeProductModifierGroups.find(
      (group) => {
        if (planSeasoningIds.has(group.id)) return false;
        const selected = readSelectedModifierIds(selectedModifiersByGroup, group.id);
        const minimum = Math.max(group.required ? 1 : 0, Number(group.minSelection || 0));
        return selected.length < minimum;
      }
    );
    if (missingRequiredGroup) {
      setModalError(`Please select an option for ${missingRequiredGroup.name}.`);
      return;
    }

    let missingNestedName = '';
    outer: for (const group of activeProductModifierGroups) {
      for (const pId of readSelectedModifierIds(selectedModifiersByGroup, group.id)) {
        const item = group.items.find((i) => i.id === pId);
        if (!item?.childGroups?.length) continue;
        for (const cg of item.childGroups) {
          const sk = nestedModifierStateKey(group.id, item.id, cg.id);
          const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
          const minimum = Math.max(cg.required ? 1 : 0, Number(cg.minSelection || 0));
          if (sel.length < minimum) {
            missingNestedName = cg.name;
            break outer;
          }
        }
      }
    }
    if (missingNestedName) {
      setModalError(`Please select an option for ${missingNestedName}.`);
      return;
    }

    let missingBundleSeasoning = '';
    for (let idx = 0; idx < bundleSlotPlan.length; idx++) {
      const sg = bundleSlotPlan[idx].seasoningGroup;
      if (!sg) continue;
      const sk = bundleSeasoningStateKey(idx, sg.id);
      const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
      const minimum = Math.max(sg.required ? 1 : 0, Number(sg.minSelection || 0));
      if (sel.length < minimum) {
        missingBundleSeasoning = sg.name;
        break;
      }
    }
    if (missingBundleSeasoning) {
      setModalError(`Please select an option for ${missingBundleSeasoning}.`);
      return;
    }

    const topSelectedOptions = activeProductModifierGroups
      .flatMap((group) => {
        const selectedItemIds = readSelectedModifierIds(selectedModifiersByGroup, group.id);
        if (!selectedItemIds.length) return [];
        return selectedItemIds
          .map((selectedItemId) => {
            const item = group.items.find((option) => option.id === selectedItemId);
            if (!item) return null;
            return {
              groupId: group.id,
              groupName: group.name,
              id: item.id,
              name: item.name,
              price: item.price,
            };
          })
          .filter(Boolean);
      })
      .filter(Boolean);

    const nestedSelectedOptions = activeProductModifierGroups.flatMap((group) => {
      const parents = readSelectedModifierIds(selectedModifiersByGroup, group.id);
      return parents.flatMap((pId) => {
        const item = group.items.find((i) => i.id === pId);
        if (!item?.childGroups?.length) return [];
        return item.childGroups.flatMap((cg) => {
          const sk = nestedModifierStateKey(group.id, item.id, cg.id);
          return readSelectedModifierIds(selectedModifiersByGroup, sk)
            .map((optId) => {
              const opt = cg.items.find((o) => o.id === optId);
              if (!opt) return null;
              return {
                groupId: cg.id,
                groupName: `${group.name} — ${item.name} — ${cg.name}`,
                id: opt.id,
                name: opt.name,
                price: opt.price,
              };
            })
            .filter(Boolean);
        });
      });
    });

    const selectedOptions = [
      ...topSelectedOptions,
      ...nestedSelectedOptions,
      ...bundleSlotPlan.flatMap((slot, idx) => {
        const sg = slot.seasoningGroup;
        if (!sg) return [];
        const sk = bundleSeasoningStateKey(idx, sg.id);
        return readSelectedModifierIds(selectedModifiersByGroup, sk)
          .map((optId) => {
            const opt = sg.items.find((o) => o.id === optId);
            if (!opt) return null;
            return {
              groupId: sg.id,
              groupName: `${sg.name} (choice ${idx + 1})`,
              id: opt.id,
              name: opt.name,
              price: opt.price,
            };
          })
          .filter(Boolean);
      }),
    ];
    const modifierAmount = selectedModifierAmount;
    const unitPrice = selectedProduct.basePrice + modifierAmount;
    const lineTotal = unitPrice * modalQuantity;

    setCartItems((prev) => [
      ...prev,
      {
        id: `${selectedProduct.id}-${Date.now()}`,
        productId: selectedProduct.id,
        name: selectedProduct.name,
        quantity: modalQuantity,
        selectedOptions,
        unitPrice,
        lineTotal,
      },
    ]);

    closeProductModal();
  };

  const updateCartItemQuantity = (itemId, nextQuantity) => {
    if (nextQuantity < 1) return;
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              quantity: nextQuantity,
              lineTotal: item.unitPrice * nextQuantity,
            }
          : item
      )
    );
  };

  const removeCartItem = (itemId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const toggleCartItemDetails = (itemId) => {
    setExpandedCartItemIds((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const openCheckout = () => {
    if (isMenuBrowseOnly) return;
    if (!cartItems.length) return;
    setIsCartDrawerOpen(false);
    setPlaceOrderError('');
    setPlaceOrderSuccess('');
    setIsOrderSuccessModalOpen(false);
    const defaultMethod =
      companyPaymentMethods.find((method) => method.key === 'card') ||
      companyPaymentMethods[0];
    if (defaultMethod) {
      setCheckoutPaymentMethod(defaultMethod.key);
    }
    const slot = selectedSlot;
    const slotTitle = String(slot?.title || '').trim();
    const usedAsapPlaceholder =
      !slot ||
      (slotTitle.toLowerCase() === 'asap' && (!slot.dateTime || String(slot.dateTime).trim() === ''));
    if (!usedAsapPlaceholder && slotTitle) {
      setCheckoutScheduleType('later');
      setScheduledTime(slotTitle);
      const now = clampToSqlDateTimeRange(new Date());
      const nextDelivery = resolvePreorderDeliveryAfterNow({
        orderNow: now,
        checkoutScheduleType: 'later',
        scheduledDate,
        scheduledTime: slotTitle,
        checkoutSlots: timeSlots,
      });
      setScheduledDate(formatCheckoutScheduleDateLabel(nextDelivery));
    } else {
      setCheckoutScheduleType('asap');
    }
    navigateTo('/review-order');
  };

  const buildOrderRef = () =>
    `${ORDER_CONFIG.locationId}${getPoscraftCompanyId()}${Date.now()}`;

  const buildPoscraftStripePaymentPayload = (amountMajor, resolvedOrderRef) =>
    buildPoscraftStripeIntentBody({
      customerId: loggedInCustomerId ? Number(loggedInCustomerId) : 0,
      locationId: ORDER_CONFIG.locationId,
      netAmount: amountMajor,
      customerName: `${customerFirstName} ${customerLastName}`.trim() || customerFirstName.trim(),
      email: customerEmail.trim(),
      serviceTypeId: checkoutServiceTypeId,
      orderRef: resolvedOrderRef,
    });

  const requestPoscraftStripeIntent = async ({ amountMajor, resolvedOrderRef }) => {
    const poscraftBody = buildPoscraftStripePaymentPayload(amountMajor, resolvedOrderRef);
    const response = await apiFetch('/actions/Payments/StripeIntent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(poscraftBody),
    });
    const payload = await response.json().catch(() => ({}));
    const message = String(payload?.message || payload?.Message || '').trim();
    if (!response.ok) {
      return {
        ok: false,
        message: message || (await parseErrorMessage(response, 'Could not start card payment.')),
        payload,
      };
    }
    const isSuccess = payload?.success ?? payload?.Success;
    if (isSuccess === false) {
      return { ok: false, message: message || 'Could not start card payment.', payload };
    }
    const clientSecret = parseStripeIntentClientSecret(payload);
    if (!clientSecret) {
      return { ok: false, message: 'Card payment could not be initialized. Please try again.', payload };
    }
    return { ok: true, clientSecret, payload };
  };

  const createStripePaymentIntent = async ({ netAmount, orderRef }) => {
    const resolvedOrderRef = String(orderRef || buildOrderRef());
    const amountMajor = resolveStripeChargeAmountMajor(
      netAmount,
      runtimeSettings.stripeTestChargeAmount
    );
    if (isBelowStripeMinimumCharge(amountMajor)) {
      throw new Error('Order total is below the minimum for card payment (£0.30).');
    }

    const result = await requestPoscraftStripeIntent({ amountMajor, resolvedOrderRef });
    if (result.ok) {
      return { clientSecret: result.clientSecret, orderRef: resolvedOrderRef, payload: result.payload };
    }
    throw new Error(result.message || 'Could not start card payment.');
  };

  const buildSaleInvoicePlaceOrderValue = (orderRef) => {
    const now = clampToSqlDateTimeRange(new Date());
    const deliverySource =
      checkoutScheduleType === 'later'
        ? resolvePreorderDeliveryAfterNow({
            orderNow: now,
            checkoutScheduleType,
            scheduledDate,
            scheduledTime,
            checkoutSlots: visibleCheckoutSlots,
          })
        : now;
    const cid = loggedInCustomerId ? Number(loggedInCustomerId) : 0;
    const omitClientDates = String(getRuntimeConfig().SALE_ORDER_OMIT_CLIENT_DATES || '').toLowerCase() === 'true';
    const minimalInvoiceDates =
      String(getRuntimeConfig().SALE_INVOICE_MINIMAL_DATES || '').toLowerCase() === 'true';
    const formatInvoiceIso = (input) => clampToSqlDateTimeRange(input).toISOString();
    const isAsap = checkoutScheduleType === 'asap';
    const paymentTypeId = Number(selectedPaymentMethod?.paymentTypeId || 0);
    if (!paymentTypeId) {
      throw new Error('Payment method is not configured. Please refresh and try again.');
    }

    // Direct Charges: customer pays subtotal + fixed platform fee (e.g. £0.50).
    const safePlatformFeeAmount = (() => {
      const n = Number(runtimeSettings?.platformFeeAmount);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })();
    const orderTotalWithPlatformFee = roundMoney2(cartSummary.totalAmount + safePlatformFeeAmount);
    const currencyTypeId =
      ORDER_CONFIG.currencyTypeId != null && Number.isFinite(Number(ORDER_CONFIG.currencyTypeId))
        ? Number(ORDER_CONFIG.currencyTypeId)
        : 0;

    const saleInvoiceDetails = cartItems.map((item) => ({
      id: 0,
      saleInvoiceId: 0,
      saleInvoiceDetailId: 0,
      productId: Number(item.productId) || 0,
      locationId: ORDER_CONFIG.locationId,
      title: item.name || '',
      description: '',
      productType: 0,
      paidQuantity: Number(item.quantity) || 0,
      quantity: Number(item.quantity) || 0,
      transactionUnit: '',
      accountId: 0,
      status: 0,
      status1: 0,
      status2: 0,
      status3: 0,
      priceOverride: false,
      rate: roundMoney2(item.unitPrice),
      taxId: 0,
      taxRate: 0,
      taxAmount: 0,
      grossAmount: roundMoney2(item.lineTotal),
      discountPercentage: 0,
      discountAmount: 0,
      serviceChargesRate: 0,
      serviceChargeExempt: false,
      serviceCharges: 0,
      netAmount: roundMoney2(item.lineTotal),
      saleInvoiceProductTag: '',
      discountTag: '',
      serialNumber: '',
      hireDays: 0,
      salesPersonId: 0,
      companyId: getPoscraftCompanyId(),
      customizations: (item.selectedOptions || []).map((option) => ({
        modifierGroupId: option.groupId,
        modifierItemId: option.id,
        name: option.name,
        price: roundMoney2(option.price),
      })),
      kitchenMessages: [],
      recipeItems: [],
      reasonLogs: [],
    }));

    const saleInvoice = {
      id: 0,
      saleQuotationId: 0,
      saleOrderId: 0,
      saleDeliveryId: 0,
      number: orderRef,
      batchId: 0,
      terminalId: 0,
      locationId: ORDER_CONFIG.locationId,
      serviceTypeId: checkoutServiceTypeId,
      employeeOne: 0,
      employeeTwo: 0,
      customerId: cid,
      customerName: `${customerFirstName} ${customerLastName}`.trim() || customerFirstName.trim() || 'Guest',
      contactNumber: customerPhone.trim(),
      earnedPoints: 0,
      isAsap,
      preOrder: !isAsap,
      storeType: 'All',
      paymentTypeId,
      couponId: 0,
      waitingTime: toNumber(selectedOrderingService?.waitingTime, 0),
      deliveryAddress: '',
      postCode: '',
      latitude: '',
      longitude: '',
      driverId: 0,
      tableNumber: 0,
      covers: 0,
      notes: orderNotes?.trim() || '',
      status: 1,
      saleInvoiceTag: '',
      saleInvoiceCode: '',
      grossAmount: orderTotalWithPlatformFee,
      discountPercentage: 0,
      discountAmount: 0,
      serviceChargesRate: 0,
      serviceChargesAmount: safePlatformFeeAmount,
      serviceChargeExempt: false,
      surcharge: 0,
      deliveryRateId: 0,
      deliveryRate: 0,
      deliveryCharges: 0,
      taxAmount: 0,
      tips: 0,
      advanceAmount: 0,
      netAmount: orderTotalWithPlatformFee,
      currencyTypeId,
      exchangeRate: 1,
      saleInvoiceDetails,
      customerPaymentDetails: [],
      customerPayments: [],
      customerRefunds: [],
      serviceTypeName: String(selectedOrderingService?.name || '').trim(),
      orderNumber: 0,
      hireDays: 0,
      paidAmount: 0,
      balanceAmount: orderTotalWithPlatformFee,
      isClosed: false,
      corbonFreeDiningExempt: false,
      companyId: getPoscraftCompanyId(),
      rounding: 0,
      reservationId: 0,
      source: 0,
      miles: 0,
      serverSaleInvoiceId: 0,
      reasonLogs: [],
      ...(omitClientDates
        ? {}
        : minimalInvoiceDates
          ? {
              deliveryDateTime: formatInvoiceIso(deliverySource),
              createdOn: formatInvoiceIso(now),
            }
          : {
              deliveryDateTime: formatInvoiceIso(deliverySource),
              createdOn: formatInvoiceIso(now),
              updatedOn: formatInvoiceIso(now),
              dueDate: formatInvoiceIso(deliverySource),
              /**
               * Omit `deliveredOn`: server should set completion; sending it on pre-orders can trigger bad defaults.
               */
            }),
    };

    /**
     * Logged-in: send `customerId` only. Guest: nested `customer` is required (same constraint as SaleOrders).
     */
    if (cid <= 0) {
      saleInvoice.customer = {
        id: 0,
        firstName: customerFirstName.trim(),
        lastName: customerLastName.trim(),
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        countryId: 0,
        contactPerson: `${customerFirstName} ${customerLastName}`.trim(),
        email: customerEmail.trim(),
        phone: customerPhone.trim(),
        companyId: getPoscraftCompanyId(),
        locationId: ORDER_CONFIG.locationId,
        guest: true,
        ...(omitClientDates || minimalInvoiceDates ? {} : { asOfDate: formatInvoiceIso(now) }),
      };
    }

    return saleInvoice;
  };

  const buildCallbackOrderPayload = ({ orderRef, saleInvoice, stripePaymentIntentId = '' }) => {
    const deliverySource = clampToSqlDateTimeRange(
      saleInvoice?.deliveryDateTime || saleInvoice?.dueDate || new Date()
    );
    const serviceType = resolveCallbackServiceType({
      selectedService: selectedOrderingService,
      checkoutServiceTypeId,
      orderConfig: ORDER_CONFIG,
    });
    // When Stripe was actually used (Direct Charges), we should report payment type as `card`.
    // Otherwise, fall back to the configured callback payment type.
    const callbackPaymentType =
      checkoutPaymentMethod === 'card' && stripePaymentIntentId
        ? 'card'
        : checkoutPaymentMethod === 'card'
          ? String(getRuntimeConfig().CALLBACK_PAYMENT_TYPE || 'card').trim().toLowerCase() || 'card'
          : 'cash';
    const toNumericString = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? String(roundMoney2(n)) : '0';
    };
    const toNumberValue = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? roundMoney2(n) : 0;
    };

    const items = (saleInvoice?.saleInvoiceDetails || []).map((row, idx) => ({
      product_name: String(row?.title || `Item ${idx + 1}`),
      ref: String(row?.productId || row?.id || idx + 1),
      rate: toNumericString(row?.rate),
      quantity: toNumericString(row?.quantity),
      subtotal: toNumberValue(row?.grossAmount),
      discountPercentage: toNumberValue(row?.discountPercentage),
      discountAmount: toNumberValue(row?.discountAmount),
      taxRate: toNumberValue(row?.taxRate),
      taxAmount: toNumberValue(row?.taxAmount),
      serviceChargeExempt: row?.serviceChargeExempt ? 1 : 0,
      serviceChargePercentage: toNumberValue(row?.serviceChargesRate),
      serviceChargeAmount: toNumberValue(row?.serviceCharges),
      taxIncluded: 1,
      netAmount: toNumberValue(row?.netAmount),
      customerNotes: '',
      options: (row?.customizations || []).map((option, oIdx) => ({
        name: String(option?.name || `Option ${oIdx + 1}`),
        ref: String(option?.modifierItemId || option?.id || oIdx + 1),
        rate: toNumericString(option?.price),
        quantity: '1',
        subtotal: toNumberValue(option?.price),
        discountPercentage: 0,
        discountAmount: 0,
        taxRate: 0,
        taxAmount: 0,
        serviceChargeExempt: 0,
        serviceChargePercentage: 0,
        serviceChargeAmount: 0,
        taxIncluded: 1,
        netAmount: toNumberValue(option?.price),
      })),
    }));

    return {
      id: String(saleInvoice?.id || 0),
      service_type: serviceType,
      location_id: Number(saleInvoice?.locationId || ORDER_CONFIG.locationId || 1),
      discountPercentage: toNumberValue(saleInvoice?.discountPercentage),
      serviceChargesRate: toNumberValue(saleInvoice?.serviceChargesRate),
      serviceChargesAmount: toNumberValue(saleInvoice?.serviceChargesAmount),
      deliveryChargesAmount: toNumberValue(saleInvoice?.deliveryCharges),
      delivery_time: formatCallbackDateTime(deliverySource),
      customerNotes: String(saleInvoice?.notes || ''),
      items,
      payment: {
        type: callbackPaymentType,
        amount: (() => {
          const orderAmount = toNumberValue(saleInvoice?.netAmount ?? cartSummary.totalAmount);
          if (callbackPaymentType === 'card' && stripePaymentIntentId) {
            return resolveStripeChargeAmountMajor(orderAmount, runtimeSettings.stripeTestChargeAmount);
          }
          return orderAmount;
        })(),
        reference: stripePaymentIntentId || undefined,
      },
      customer: {
        id: String(saleInvoice?.customerId || loggedInCustomerId || 0),
        email: customerEmail.trim(),
        first_name: customerFirstName.trim(),
        last_name: customerLastName.trim(),
        phone: customerPhone.trim(),
        address_1: String(saleInvoice?.deliveryAddress || ''),
        address_2: '',
        postal_code: String(saleInvoice?.postCode || ''),
        city: '',
        state: '',
        latitude: String(saleInvoice?.latitude || ''),
        longitude: String(saleInvoice?.longitude || ''),
        delivery_notes: String(saleInvoice?.notes || ''),
        sms_marketing: 0,
        email_marketing: 0,
      },
      source_order_ref: String(orderRef || ''),
    };
  };

  const placeOrder = async ({ stripePaymentIntentId = '', orderRef: orderRefInput = '' } = {}) => {
    if (!canPlaceOrder || isPlacingOrder) return;
    setIsPlacingOrder(true);
    setPlaceOrderError('');
    setPlaceOrderSuccess('');

    try {
      if (checkoutScheduleType === 'asap') {
        if (!isStoreOpenNow()) {
          throw new Error(STORE_CLOSED_ORDER_MESSAGE);
        }
      } else {
        const scheduledInstant = composeLocalDateTimeFromScheduleLabel(
          normalizeScheduleDateLabel(scheduledDate),
          scheduledTime
        );
        if (!scheduledInstant || !isWithinStoreHours(scheduledInstant)) {
          throw new Error('Please choose a collection time when we are open.');
        }
      }

      const orderRef = String(orderRefInput || buildOrderRef());

      if (checkoutPaymentMethod === 'card' && !stripePaymentIntentId) {
        throw new Error('Card payment is missing. Please pay and try again.');
      }

      const saleInvoice = buildSaleInvoicePlaceOrderValue(orderRef);
      const callbackPayload = buildCallbackOrderPayload({ orderRef, saleInvoice, stripePaymentIntentId });
      const authKey = getCallbackAuthorizationKey() || getCallbackConfig().apiKey;
      if (!authKey) {
        throw new Error(
          'Missing CALLBACK_AUTHORIZATION_KEY. Set it in public/runtime-config.js or .env (local dev).'
        );
      }
      const response = await fetch(
        callbackOrderUrl(getPoscraftCompanyId(), runtimeSettings.useLocalCallbackProxy),
        {
        method: 'POST',
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(callbackPayload),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Callback PlaceOrder failed with status ${response.status}`);
        throw new Error(formatCallbackPlaceOrderError(message));
      }

      const result = await response.json().catch(() => ({}));
      const isSuccess = Boolean(result?.success ?? result?.Success ?? (result?.StatusCode ? Number(result.StatusCode) < 400 : true));
      const responseMessage = result?.message || result?.Message || '';
      if (!isSuccess) {
        throw new Error(formatCallbackPlaceOrderError(responseMessage || 'Order could not be created via callback API.'));
      }

      setPlaceOrderSuccess(responseMessage || 'Order has been placed successfully.');
      setCartItems([]);
      setOrderNotes('');
      setIsScheduleExpanded(false);
      setCheckoutScheduleType('asap');
      setIsOrderSuccessModalOpen(true);
    } catch (error) {
      console.error('Failed to place order:', error);
      const detail = error instanceof Error ? String(error.message || '').trim() : '';
      setPlaceOrderError(detail || PLACE_ORDER_FAILURE_MESSAGE);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const onSidebarItemClick = (item) => {
    if (item === 'ORDER NOW') {
      navigateTo('/');
      return;
    }

    if (item === 'CHIPPY POINTS & STAMPS') {
      navigateTo('/add-loyalty-wallet');
      setIsProfileOpen(false);
      return;
    }

    if (item === 'FAVOURITE ORDERS') {
      navigateTo('/favourite-orders');
      setIsProfileOpen(false);
      setIsPastOrdersFilterOpen(false);
      return;
    }

    if (item === 'PAST ORDERS') {
      navigateTo('/past-orders');
      setIsProfileOpen(false);
      setIsPastOrdersFilterOpen(false);
      return;
    }

    if (item === 'LOYALTY HISTORY') {
      navigateTo('/loyalty-history');
      setIsProfileOpen(false);
      setIsPastOrdersFilterOpen(false);
      return;
    }

    if (item === 'PROFILE') {
      setIsProfileOpen((prev) => !prev);
      return;
    }

    if (item === 'SIGN IN') {
      setIsSidebarOpen(false);
      setSignInError('');
      setIsSignInModalOpen(true);
      return;
    }

    if (item === 'SIGN OUT') {
      setIsLoggedIn(false);
      setLoggedInCustomerId(null);
      setCurrentCustomerName('');
      setProfileDob('');
      setProfileGender('');
      setPasswordCurrent('');
      setPasswordNew('');
      setPasswordConfirm('');
      setChangePasswordError('');
      setChangePasswordSuccess('');
      setProfileSaveSuccess('');
      setProfileSaveError('');
      setProfileLoadError('');
      try {
        localStorage.removeItem(CUSTOMER_SESSION_KEY);
      } catch (_error) {
        // Ignore storage failures and proceed with in-memory sign out.
      }
      setIsProfileOpen(false);
      navigateTo('/');
      return;
    }
  };

  const handleSignIn = async () => {
    const email = signInEmail.trim();
    const password = signInPassword.trim();
    if (!email || !password || isSigningIn) return;

    setIsSigningIn(true);
    setSignInError('');
    setSignInNotice('');

    try {
      const response = await apiFetch('/actions/Customers/Login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          platformType: 0,
        }),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Login failed with status ${response.status}`);
        throw new Error(message);
      }

      const customer = await response.json().catch(() => ({}));
      const customerId = Number(customer?.id || 0);
      const loginSuccessFlag = customer?.success ?? customer?.Success;
      const hasExplicitFailure = loginSuccessFlag === false;
      const hasValidCustomerRecord = customerId > 0;
      if (hasExplicitFailure || !hasValidCustomerRecord) {
        const loginMessage = customer?.message || customer?.Message || 'Invalid email or password.';
        throw new Error(loginMessage);
      }

      const firstName = (customer?.firstName || '').trim();
      const lastName = (customer?.lastName || '').trim();
      const customerName = `${firstName} ${lastName}`.trim();

      setIsLoggedIn(true);
      setLoggedInCustomerId(customerId);
      setCurrentCustomerName(customerName || email);
      setCustomerFirstName(firstName || customerFirstName);
      setCustomerLastName(lastName);
      setCustomerEmail((customer?.email || email).trim());
      setCustomerPhone((customer?.phone || '').trim() || customerPhone);
      let profileWidToStore = '';
      let profileDobToStore = '';
      let profileGenderToStore = '';
      try {
        const prevRaw = localStorage.getItem(CUSTOMER_SESSION_KEY);
        if (prevRaw) {
          const prev = JSON.parse(prevRaw);
          if (Number(prev?.customerId) === customerId) {
            if (prev.profileWid) profileWidToStore = String(prev.profileWid).trim();
            if (isYmd(prev.profileDob)) profileDobToStore = String(prev.profileDob).trim();
            const pg = normalizeGenderFromApi(prev.profileGender) || String(prev.profileGender || '').trim();
            if (pg) profileGenderToStore = pg;
          }
        }
      } catch (_error) {
        // Ignore and start with empty WID.
      }
      setProfileWid(profileWidToStore);
      setProfileDob(profileDobToStore);
      setProfileGender(profileGenderToStore);
      try {
        localStorage.setItem(
          CUSTOMER_SESSION_KEY,
          JSON.stringify({
            isLoggedIn: true,
            customerId,
            currentCustomerName: customerName || email,
            customerFirstName: firstName || customerFirstName,
            customerLastName: lastName,
            customerEmail: (customer?.email || email).trim(),
            customerPhone: (customer?.phone || '').trim() || customerPhone,
            profileWid: profileWidToStore,
            profileDob: profileDobToStore,
            profileGender: profileGenderToStore,
          })
        );
      } catch (_error) {
        // Ignore storage failures and keep session in-memory.
      }
      setIsSignInModalOpen(false);
      setSignInEmail('');
      setSignInPassword('');
    } catch (error) {
      setSignInError(error?.message || 'Could not sign in. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const resetForgotPasswordModal = () => {
    setForgotPasswordStep('email');
    setForgotPasswordEmail('');
    setForgotResetCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  const handleForgotPasswordRequest = async () => {
    const email = forgotPasswordEmail.trim();
    if (!email || isSendingForgotPassword) return;

    setIsSendingForgotPassword(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      const response = await apiFetch('/actions/Customers/ForgetPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Request failed with status ${response.status}`);
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const isSuccess = Boolean(result?.success ?? result?.Success);
      const responseMessage = result?.message || result?.Message || '';
      if (!isSuccess) {
        throw new Error(responseMessage || 'Could not send reset instructions.');
      }

      setForgotPasswordSuccess(friendlyForgetPasswordSentMessage(responseMessage));
      setForgotPasswordStep('reset');
    } catch (error) {
      setForgotPasswordError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSendingForgotPassword(false);
    }
  };

  const handleForgotPasswordComplete = async () => {
    const email = forgotPasswordEmail.trim();
    const code = forgotResetCode.trim();
    const nextPass = forgotNewPassword.trim();
    const confirmPass = forgotConfirmPassword.trim();

    if (!email || !code || !nextPass || !confirmPass) {
      setForgotPasswordError('Please fill in all fields.');
      return;
    }
    if (nextPass !== confirmPass) {
      setForgotPasswordError('New password and confirm password do not match.');
      return;
    }
    if (isCompletingPasswordReset) return;

    setIsCompletingPasswordReset(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      const validatePath = `/actions/Customers/ValidateResetPasswordCode/${encodeURIComponent(email)}/${encodeURIComponent(code)}`;
      const validateResponse = await apiFetch(validatePath, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!validateResponse.ok) {
        const message = await parseErrorMessage(validateResponse, `Invalid or expired code (${validateResponse.status})`);
        throw new Error(message);
      }

      await validateResponse.json().catch(() => ({}));

      const resetResponse = await apiFetch('/actions/Customers/ResetPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: nextPass, resetCode: code }),
      });

      if (!resetResponse.ok) {
        const message = await parseErrorMessage(resetResponse, `Reset password failed with status ${resetResponse.status}`);
        throw new Error(message);
      }

      const resetResult = await resetResponse.json().catch(() => ({}));
      const resetOk = Boolean(resetResult?.success ?? resetResult?.Success);
      const resetMessage = String(resetResult?.message || resetResult?.Message || '').trim();
      if (!resetOk) {
        throw new Error(resetMessage || 'Could not reset password.');
      }

      const signInNoticeText =
        resetMessage && (/\s/.test(resetMessage) || /password/i.test(resetMessage))
          ? resetMessage
          : 'Your password was reset. Sign in with your new password.';

      setIsForgotPasswordModalOpen(false);
      resetForgotPasswordModal();
      setSignInEmail(email);
      setSignInPassword('');
      setSignInError('');
      setSignInNotice(signInNoticeText);
      setIsSignInModalOpen(true);
    } catch (error) {
      setForgotPasswordError(error?.message || 'Could not reset password. Please try again.');
    } finally {
      setIsCompletingPasswordReset(false);
    }
  };

  const handleSignUp = async () => {
    if (isSigningUp) return;

    const firstName = signUpFirstName.trim();
    const lastName = signUpLastName.trim();
    const email = signUpEmail.trim();
    const password = signUpPassword.trim();
    const confirmPassword = signUpConfirmPassword.trim();
    const phone = signUpPhone.trim();

    if (!firstName || !lastName || !email || !password || !phone) {
      setSignUpError('Please fill all required fields.');
      return;
    }
    if (password !== confirmPassword) {
      setSignUpError('Password and confirm password do not match.');
      return;
    }

    setIsSigningUp(true);
    setSignUpError('');
    setSignUpSuccess('');

    try {
      const payload = {
        firstName,
        lastName,
        email,
        password,
        phone,
        optIn: true,
        optInEmail: true,
        optInSMS: true,
        platformType: 0,
      };

      const response = await apiFetch('/actions/Customers/SignUp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Sign up failed with status ${response.status}`);
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const isSuccess = Boolean(result?.success ?? result?.Success);
      const responseMessage = result?.message || result?.Message || '';
      if (!isSuccess) {
        throw new Error(responseMessage || 'Could not register account.');
      }

      setSignUpSuccess(responseMessage || 'Registration successful. Please sign in.');
      setSignUpFirstName('');
      setSignUpLastName('');
      setSignUpEmail('');
      setSignUpPassword('');
      setSignUpConfirmPassword('');
      setSignUpPhone('');

      setSignInEmail(email);
      setIsSignUpModalOpen(false);
      setIsSignInModalOpen(true);
    } catch (error) {
      setSignUpError(error?.message || 'Could not register account. Please try again.');
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!loggedInCustomerId || isProfileSaving) return;

    setIsProfileSaving(true);
    setProfileSaveError('');
    setProfileSaveSuccess('');

    try {
      const genderKey = profileGender.trim().toLowerCase();
      const genderValue =
        genderKey && Object.prototype.hasOwnProperty.call(GENDER_SELECT_TO_API, genderKey)
          ? GENDER_SELECT_TO_API[genderKey]
          : profileGender.trim() || null;

      const dobYmd = String(profileDob || '').trim();
      const dobValid = /^\d{4}-\d{2}-\d{2}$/.test(dobYmd);
      const dateOfBirthIso = dobValid ? fromDateInputToIso(dobYmd) : null;

      const customerUpdate = {
        id: Number(loggedInCustomerId),
        firstName: customerFirstName.trim(),
        lastName: customerLastName.trim(),
        phone: customerPhone.trim(),
        addressLine1: profileAddressLine1.trim(),
        addressLine2: profileAddressLine2.trim(),
        countyTown: profileCountyTown.trim(),
        postCode: profilePostCode.trim(),
      };

      if (genderValue) {
        customerUpdate.gender = genderValue;
        customerUpdate.Gender = genderValue;
      }
      if (dateOfBirthIso) {
        customerUpdate.dateOfBirth = dateOfBirthIso;
        customerUpdate.DateOfBirth = dateOfBirthIso;
      }

      const response = await apiFetch('/actions/Customers/UpdateProfile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerUpdate),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Update failed with status ${response.status}`);
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const isSuccess = Boolean(result?.success ?? result?.Success);
      const responseMessage = result?.message || result?.Message || '';
      if (!isSuccess) {
        throw new Error(responseMessage || 'Could not update profile.');
      }

      setProfileSaveSuccess(responseMessage || 'Profile updated.');
      profileFetchKeepSuccessMessage.current = true;
      setProfileReloadNonce((n) => n + 1);
      try {
        const saved = localStorage.getItem(CUSTOMER_SESSION_KEY);
        if (saved) {
          const session = JSON.parse(saved);
          session.profileWid = profileWid.trim();
          session.customerFirstName = customerFirstName.trim();
          session.customerLastName = customerLastName.trim();
          session.customerPhone = customerPhone.trim();
          const dobSave = String(profileDob || '').trim();
          session.profileDob = isYmd(dobSave) ? dobSave : '';
          session.profileGender = normalizeGenderFromApi(profileGender) || '';
          localStorage.setItem(CUSTOMER_SESSION_KEY, JSON.stringify(session));
        }
      } catch (_error) {
        // Ignore storage merge failures.
      }
    } catch (error) {
      setProfileSaveError(error?.message || 'Could not update profile. Please try again.');
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!loggedInCustomerId || isChangingPassword) return;

    const current = passwordCurrent.trim();
    const next = passwordNew.trim();
    const confirm = passwordConfirm.trim();

    setChangePasswordError('');
    setChangePasswordSuccess('');

    if (!current || !next || !confirm) {
      setChangePasswordError('Please fill in all password fields.');
      return;
    }
    if (next !== confirm) {
      setChangePasswordError('New password and confirm password do not match.');
      return;
    }
    if (next === current) {
      setChangePasswordError('New password must be different from your current password.');
      return;
    }

    setIsChangingPassword(true);
    try {
      const changePassword = {
        id: Number(loggedInCustomerId),
        currentPassword: current,
        newPassword: next,
      };

      const response = await apiFetch('/actions/Customers/ChangePassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(changePassword),
      });

      if (!response.ok) {
        const message = await parseErrorMessage(response, `Change password failed with status ${response.status}`);
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const isSuccess = Boolean(result?.success ?? result?.Success);
      const responseMessage = result?.message || result?.Message || '';
      if (!isSuccess) {
        throw new Error(responseMessage || 'Could not change password.');
      }

      setChangePasswordSuccess(responseMessage || 'Password updated.');
      setPasswordCurrent('');
      setPasswordNew('');
      setPasswordConfirm('');
    } catch (error) {
      setChangePasswordError(error?.message || 'Could not change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    const onPopState = () => setCurrentPath(window.location.pathname || '/');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CUSTOMER_SESSION_KEY);
      if (!saved) return;
      const session = JSON.parse(saved);
      if (!session?.isLoggedIn) return;

      setIsLoggedIn(true);
      setCurrentCustomerName(session.currentCustomerName || '');
      setCustomerFirstName(session.customerFirstName || '');
      setCustomerLastName(session.customerLastName || '');
      setCustomerEmail(session.customerEmail || '');
      setCustomerPhone(session.customerPhone || '');
      const restoredId = Number(session.customerId || 0);
      setLoggedInCustomerId(restoredId > 0 ? restoredId : null);
      setProfileWid(String(session.profileWid || '').trim());
      const sdob = String(session.profileDob || '').trim();
      setProfileDob(isYmd(sdob) ? sdob : '');
      setProfileGender(normalizeGenderFromApi(session.profileGender) || '');
    } catch (_error) {
      // Ignore invalid session payload and continue with defaults.
    }
  }, []);

  useEffect(() => {
    if (currentPath !== '/profile/personal-details' || !loggedInCustomerId) return undefined;

    let cancelled = false;
    const loadCustomerProfile = async () => {
      setIsProfileLoading(true);
      setProfileLoadError('');
      setProfileSaveError('');
      if (!profileFetchKeepSuccessMessage.current) {
        setProfileSaveSuccess('');
      }
      try {
        const response = await apiFetch(`/data/Customers/${loggedInCustomerId}`);
        if (!response.ok) {
          const message = await parseErrorMessage(response, `Could not load profile (${response.status})`);
          throw new Error(message);
        }
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;

        const card = data.cardNumber != null && String(data.cardNumber).trim() !== '' ? String(data.cardNumber).trim() : '';
        setProfileMemberNo(card || String(data.id ?? data.Id ?? '').trim());
        setCustomerFirstName(String(data.firstName ?? data.FirstName ?? '').trim());
        setCustomerLastName(String(data.lastName ?? data.LastName ?? '').trim());
        setCustomerEmail(String(data.email ?? data.Email ?? '').trim());
        setCustomerPhone(String(data.phone ?? data.Phone ?? '').trim());
        const rawDob = data.dateOfBirth ?? data.DateOfBirth;
        let nextDob = toDateInputValue(rawDob);
        let nextGender = normalizeGenderFromApi(data.gender ?? data.Gender);
        const extras = readSessionProfileExtras(loggedInCustomerId);
        if (!nextDob && extras.profileDob) nextDob = extras.profileDob;
        if (!nextGender && extras.profileGender) nextGender = extras.profileGender;
        setProfileDob(nextDob);
        setProfileGender(nextGender);
        setProfileAddressLine2(String(data.address2 ?? data.Address2 ?? '').trim());
        const city = String(data.city ?? data.City ?? '').trim();
        const state = String(data.state ?? data.State ?? '').trim();
        setProfileCountyTown([city, state].filter(Boolean).join(', ') || city || state);
        setProfilePostCode(String(data.zip ?? data.Zip ?? data.postCode ?? data.PostCode ?? '').trim());
        setProfileAddressLine1(String(data.address1 ?? data.Address1 ?? '').trim());
      } catch (error) {
        if (!cancelled) {
          setProfileLoadError(error?.message || 'Could not load profile.');
        }
      } finally {
        if (!cancelled) {
          setIsProfileLoading(false);
          profileFetchKeepSuccessMessage.current = false;
        }
      }
    };

    loadCustomerProfile();
    return () => {
      cancelled = true;
    };
  }, [currentPath, loggedInCustomerId, profileReloadNonce]);

  useEffect(() => {
    let cancelled = false;
    const bootstrapOrdering = async () => {
      setIsServiceTypesLoading(true);
      setProductsError('');
      try {
        const loc = ORDER_CONFIG.locationId;
        const res = await apiFetch(`/data/Locations/${loc}/MobileAppServiceTypes`);
        if (!res.ok) {
          throw new Error(`Could not load services (${res.status})`);
        }
        const data = await res.json();
        const all = extractApiArray(data);
        const filtered = all.filter((row) => row.availableOnPos === true && row.active !== false);
        if (cancelled) return;
        setPosServiceTypes(filtered);

        const saved = readOrderingContext();
        if (saved?.serviceTypeId && saved?.onlineOrderingMenuLevelId) {
          const svc = filtered.find((s) => Number(s.id) === Number(saved.serviceTypeId));
          if (svc) {
            setSelectedOrderingService(svc);
            setSelectedSlot(
              saved.slotTitle || saved.slotDateTime
                ? {
                    title: saved.slotTitle || 'Saved time',
                    dateTime: saved.slotDateTime || '',
                    isAvailable: true,
                  }
                : null
            );
            setIsMenuLoading(true);
            try {
              const built = await buildMenuSectionsFromLevelId(Number(saved.onlineOrderingMenuLevelId));
              if (!cancelled) {
                if (built.length) {
                  setSections(built);
                  setOrderingSetupStep('ready');
                  setModifierGroupsById({});
                  setModifierLinksByProductId({});
                } else {
                  setOrderingSetupStep('service');
                }
              }
            } catch (error) {
              if (!cancelled) {
                console.error(error);
                setOrderingSetupStep('service');
              }
            } finally {
              if (!cancelled) {
                setIsMenuLoading(false);
              }
            }
            return;
          }
        }
        setOrderingSetupStep('service');
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setProductsError('Could not load ordering services. Check Poscraft token, company id, and location id.');
          setSections([]);
          setOrderingSetupStep('service');
          setModifierGroupsById({});
          setModifierLinksByProductId({});
        }
      } finally {
        if (!cancelled) {
          setIsServiceTypesLoading(false);
        }
      }
    };

    void bootstrapOrdering();
    return () => {
      cancelled = true;
    };
  }, [ORDER_CONFIG.locationId, siteConfigEpoch]);

  useEffect(() => {
    if (orderingSetupStep !== 'slot') return;
    const avail = timeSlots.filter((s) => s.isAvailable !== false);
    if (!avail.length) {
      setPendingSlot(null);
      return;
    }
    setPendingSlot((prev) => {
      if (
        prev &&
        avail.some(
          (s) => s.title === prev.title && String(s.dateTime ?? '') === String(prev.dateTime ?? '')
        )
      ) {
        return prev;
      }
      return avail[0];
    });
  }, [orderingSetupStep, timeSlots]);

  useEffect(() => {
    const lock =
      currentPath === '/' &&
      (orderingSetupStep === 'slot' || (orderingSetupStep === 'service' && !isServiceTypesLoading));
    if (!lock) return undefined;
    documentScrollLock.lock();
    return () => {
      documentScrollLock.unlock();
    };
  }, [currentPath, orderingSetupStep, isServiceTypesLoading]);

  useEffect(() => {
    if (!REMOVED_VOUCHER_ROUTES.includes(currentPath)) return;
    navigateTo('/');
  }, [currentPath]);

  useEffect(() => {
    if (currentPath !== '/review-order') return undefined;
    let cancelled = false;
    const loadCheckoutSlots = async () => {
      setIsCheckoutSlotsLoading(true);
      setCheckoutSlotsError('');
      try {
        const loc = ORDER_CONFIG.locationId;
        const sid = checkoutServiceTypeId;
        const slotDate =
          parseScheduleDateLabelToLocalDate(scheduledDate) || new Date();
        const res = await apiFetch(
          locationServiceTypeSlotsPath(loc, sid, formatLocalDateYmd(slotDate))
        );
        if (!res.ok) {
          const message = await parseErrorMessage(res, `Could not load times (${res.status})`);
          throw new Error(message);
        }
        const payload = await res.json();
        const slots = extractApiArray(payload).filter((s) => s.isAvailable !== false);
        if (cancelled) return;
        setCheckoutSlots(slots);
      } catch (error) {
        if (!cancelled) {
          setCheckoutSlots([]);
          setCheckoutSlotsError(error?.message || 'Could not load available times.');
        }
      } finally {
        if (!cancelled) {
          setIsCheckoutSlotsLoading(false);
        }
      }
    };
    void loadCheckoutSlots();
    return () => {
      cancelled = true;
    };
  }, [currentPath, ORDER_CONFIG.locationId, checkoutServiceTypeId, scheduledDate]);

  useEffect(() => {
    if (currentPath !== '/review-order' || checkoutScheduleType !== 'later') return;
    if (!visibleCheckoutSlots.length) return;
    setScheduledTime((prev) =>
      visibleCheckoutSlots.some((s) => s.title === prev) ? prev : visibleCheckoutSlots[0].title
    );
  }, [currentPath, checkoutScheduleType, visibleCheckoutSlots]);

  useEffect(() => {
    if (currentPath !== '/review-order' || checkoutScheduleType !== 'later') return;
    const now = clampToSqlDateTimeRange(new Date());
    const nextDelivery = resolvePreorderDeliveryAfterNow({
      orderNow: now,
      checkoutScheduleType,
      scheduledDate,
      scheduledTime,
      checkoutSlots: visibleCheckoutSlots,
    });
    const nextDateLabel = formatCheckoutScheduleDateLabel(nextDelivery);
    if (nextDateLabel !== scheduledDate) {
      setScheduledDate(nextDateLabel);
    }
  }, [currentPath, checkoutScheduleType, scheduledDate, scheduledTime, visibleCheckoutSlots]);

  useEffect(() => {
    if (!sections.length) return;
    if (sections.some((section) => section.title === activeCategory)) return;
    setActiveCategory(sections[0].title);
  }, [sections, activeCategory]);

  useEffect(() => {
    if (!shouldUseDevProxy) return;

    const loadRuntimeSettings = async () => {
      try {
        const response = await fetch('/api/runtime-config', {
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) return;
        const config = await response.json();
        const siteIdsChanged = setPoscraftSiteIds({
          companyId: config?.companyId,
          locationId: config?.defaultLocationId,
        });
        if (siteIdsChanged) {
          clearOrderingContext();
          setSiteConfigEpoch((n) => n + 1);
        }
        setRuntimeSettings((prev) => ({
          ...prev,
          stripePublishableKey: config?.stripePublishableKey || '',
          stripeConnectAccountId:
            config && Object.prototype.hasOwnProperty.call(config, 'stripeConnectAccountId')
              ? String(config?.stripeConnectAccountId || '').trim()
              : String(prev.stripeConnectAccountId || '').trim(),
          defaultLocationId: Number(config?.defaultLocationId || prev.defaultLocationId || 1),
          collectServiceTypeId: Number(config?.collectServiceTypeId || prev.collectServiceTypeId || 1),
          deliveryServiceTypeId: Number(config?.deliveryServiceTypeId || prev.deliveryServiceTypeId || 2),
          platformFeeAmount: (() => {
            const n = Number(config?.platformFeeAmount);
            return Number.isFinite(n) && n >= 0 ? n : prev.platformFeeAmount;
          })(),
          applicationFeePercent: (() => {
            const n = Number(config?.applicationFeePercent);
            return Number.isFinite(n) && n >= 0 ? n : prev.applicationFeePercent;
          })(),
          stripeTestChargeAmount:
            config && Object.prototype.hasOwnProperty.call(config, 'stripeTestChargeAmount')
              ? (() => {
                  const n = Number(config.stripeTestChargeAmount);
                  return Number.isFinite(n) && n > 0 ? n : 0;
                })()
              : prev.stripeTestChargeAmount,
          useLocalCallbackProxy: Boolean(config?.useLocalCallbackProxy),
          currencyTypeId: (() => {
            const n = Number(config?.defaultCurrencyTypeId);
            if (Number.isFinite(n) && n > 0) return n;
            return prev.currencyTypeId;
          })(),
        }));
      } catch (error) {
        console.warn('Could not load runtime settings:', error);
      }
    };

    loadRuntimeSettings();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCompanyProfile = async () => {
      try {
        const response = await apiFetch(`/data/Company/${getPoscraftCompanyId()}`);
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        const name = String(payload?.name ?? payload?.Name ?? '').trim();
        if (!cancelled && name) {
          setResolvedCompanyName(name);
        }
      } catch (_error) {
        // Display name stays empty until company endpoint succeeds.
      }
    };

    const loadStoreLocation = async () => {
      const locId = Number(getRuntimeConfig().DEFAULT_LOCATION_ID || getPoscraftLocationId() || 1);
      try {
        const response = await apiFetch(`/data/Locations/${locId}`);
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        if (cancelled) return;
        const locName = readLocationDisplayName(payload);
        if (locName) {
          setResolvedLocationName(locName);
        }
        const address = formatStoreAddressFromLocation(payload);
        const phone = readStorePhoneFromLocation(payload);
        const fallback = readStoreContactFromConfig();
        setStoreContact({
          address: address || fallback.address,
          phone: phone || fallback.phone,
        });
      } catch (_error) {
        if (!cancelled) {
          setStoreContact(readStoreContactFromConfig());
        }
      }
    };

    void loadCompanyProfile();
    void loadStoreLocation();
    return () => {
      cancelled = true;
    };
  }, [siteConfigEpoch]);

  const loadPaymentTypesFromApi = useCallback(async () => {
    const seq = ++paymentTypesLoadSeq.current;
    setIsPaymentMethodsLoading(true);

    try {
      await waitForRuntimeConfigReady();
      if (seq !== paymentTypesLoadSeq.current) return;

      const response = await apiFetch(`/data/Company/${getPoscraftCompanyId()}/PaymentTypes`);
      if (seq !== paymentTypesLoadSeq.current) return;

      const payload = response.ok ? await response.json().catch(() => []) : [];
      const methods = normalizeCompanyPaymentTypes(payload);
      const stripeFromApi = parseStripeSettingsFromPaymentTypes(payload);

      if (seq !== paymentTypesLoadSeq.current) return;

      setCompanyPaymentMethods(methods);

      if (stripeFromApi?.publishableKey) {
        setRuntimeSettings((prev) => ({
          ...prev,
          stripePublishableKey: stripeFromApi.publishableKey,
          stripeConnectAccountId: stripeFromApi.connectAccountId || prev.stripeConnectAccountId,
        }));
      }
    } catch (_error) {
      if (seq === paymentTypesLoadSeq.current) {
        setCompanyPaymentMethods([]);
      }
    } finally {
      if (seq === paymentTypesLoadSeq.current) {
        setIsPaymentMethodsLoading(false);
      }
    }
  }, []);

  /** Fresh PaymentTypes on every full page load (not from saved/cached list). */
  useEffect(() => {
    void loadPaymentTypesFromApi();
  }, [siteConfigEpoch, loadPaymentTypesFromApi]);

  useEffect(() => {
    if (!companyPaymentMethods.length) return;
    const keys = companyPaymentMethods.map((method) => method.key);
    if (!keys.includes(checkoutPaymentMethod)) {
      setCheckoutPaymentMethod(keys[0]);
    }
  }, [companyPaymentMethods, checkoutPaymentMethod]);

  useEffect(() => {
    if (!filteredSections.length) return;
    if (filteredSections.some((section) => section.title === activeCategory)) return;
    setActiveCategory(filteredSections[0].title);
  }, [filteredSections, activeCategory]);

  useEffect(() => {
    const activeButton = categoryButtonRefs.current[activeCategory];
    const scrollContainer = categoryScrollRef.current;
    if (!activeButton || !scrollContainer) return;
    activeButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeCategory]);

  useEffect(() => {
    if (!filteredSections.length) return undefined;

    const stickyOffset = 130;
    let ticking = false;

    const updateActiveCategoryFromScroll = () => {
    const sectionElements = filteredSections
      .map((section) => sectionRefs.current[section.title])
      .filter(Boolean);

      if (!sectionElements.length) return;

      const nearPageBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;

      if (nearPageBottom) {
        const lastSection = sectionElements[sectionElements.length - 1];
        const lastSectionTitle = lastSection?.getAttribute('data-section-title');
        if (lastSectionTitle && lastSectionTitle !== activeCategory) {
          setActiveCategory(lastSectionTitle);
        }
        return;
      }

      const passedSections = sectionElements.filter(
        (element) => element.getBoundingClientRect().top - stickyOffset <= 0
      );

      const nextElement = passedSections.length
        ? passedSections[passedSections.length - 1]
        : sectionElements[0];

      const nextActiveCategory = nextElement.getAttribute('data-section-title');
      if (!nextActiveCategory || nextActiveCategory === activeCategory) return;
      setActiveCategory(nextActiveCategory);
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateActiveCategoryFromScroll();
        ticking = false;
      });
    };

    updateActiveCategoryFromScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [filteredSections, activeCategory]);

  useEffect(() => {
    const shouldLockScroll =
      currentPath === '/review-order';
    if (!shouldLockScroll) return undefined;

    documentScrollLock.lock();

    return () => {
      documentScrollLock.unlock();
    };
  }, [currentPath]);

  useEffect(() => {
    if (!isAllergenNoticeOpen) return undefined;
    documentScrollLock.lock();
    return () => {
      documentScrollLock.unlock();
    };
  }, [isAllergenNoticeOpen]);

  const navigateTo = (path) => {
    const next = path || '/';
    if (window.location.pathname !== next) {
      window.history.pushState({}, '', next);
    }
    setCurrentPath(next);
    setIsSidebarOpen(false);
  };

  const handleCloseOrderSuccessModal = () => {
    setIsOrderSuccessModalOpen(false);
    setPlaceOrderSuccess('');
    navigateTo('/');
  };

  const goToProfilePersonalDetails = () => {
    navigateTo('/profile/personal-details');
    setIsProfileOpen(false);
  };

  const goToProfileChangePassword = () => {
    navigateTo('/profile/change-password');
    setIsProfileOpen(false);
  };

  const goToProfileDeleteAccount = () => {
    navigateTo('/profile/delete-account');
    setIsProfileOpen(false);
  };

  const isProfileSubmenuRoute = ['/profile/personal-details', '/profile/change-password', '/profile/delete-account'].includes(currentPath);
  const shouldShowProfileSubmenu = isProfileOpen || isProfileSubmenuRoute;
  const isSidebarItemActive = (item) => {
    if (item === 'CHIPPY POINTS & STAMPS') return currentPath === '/add-loyalty-wallet';
    if (item === 'FAVOURITE ORDERS') return currentPath === '/favourite-orders';
    if (item === 'PAST ORDERS') return currentPath === '/past-orders';
    if (item === 'LOYALTY HISTORY') return currentPath === '/loyalty-history';
    return false;
  };

  const showProductBundleWizard = Boolean(
    selectedProduct?.isMealDealBundle && bundleSlotPlan.length > 0 && bundleFlowPhase !== 'standard'
  );

  return (
    <div className="page">
      {isAllergenNoticeOpen ? (
        <div className="allergen-notice-overlay" role="presentation">
          <div
            className="allergen-notice-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Allergen information and calorie guidance"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="allergen-notice-body">
              <p>
                The management of allergens is something we take very seriously. Please speak to a member of staff if you
                have any questions.
              </p>
              {/* <p>
                Please also be advised that a range of products are fried in our pans; we&apos;re here to help if you have
                any queries.
              </p> */}
              <p className="allergen-notice-kcal">Adults need around 2000 kcal a day</p>
            </div>
            <div className="allergen-notice-brand">
              <img
                className="allergen-notice-brand-logo"
                src={brandLogoUrl(assetBase)}
                alt={companyDisplayName}
                width={160}
                height={48}
              />
            </div>
            <button
              type="button"
              className="allergen-notice-acknowledge"
              onClick={() => setIsAllergenNoticeOpen(false)}
            >
              Acknowledge
            </button>
          </div>
        </div>
      ) : null}
      <header className="topbar">
        <button className="menu-btn" aria-label="Open menu" onClick={() => setIsSidebarOpen(true)}>
        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" style={{ color: '#fff', fontSize: '24px' }}><path d="M32 96v64h448V96H32zm0 128v64h448v-64H32zm0 128v64h448v-64H32z"></path></svg>
        </button>
        {currentPath === '/' ? (
          <div className="mobile-topbar-pickup" role="status" aria-live="polite">
            <span className="mobile-topbar-pickup-store">{companyDisplayName}</span>
            <span className="mobile-topbar-pickup-sub" onClick={resetOrderingFlow}>
              {isMenuBrowseOnly
                ? 'Viewing menu only'
                : orderingSetupStep === 'ready' && selectedOrderingService
                  ? `${selectedOrderingService.name} — ${selectedSlot?.title || 'ASAP'}`
                  : 'Choose service & time to view menu'}
                <ChevronDown size={14} style={{position: 'relative', top: '3px', left: '3px'}} />
            </span>
          </div>
        ) : null}
        {/* {currentPath === '/' ? (
          <button className="mobile-topbar-center" onClick={() => setIsModeMenuOpen((prev) => !prev)} aria-label="Toggle order mode">
            <span>Toff&apos;s of Muswell Hill</span>
            <span style={{display: 'flex', alignItems: 'center', gap: '4px',fontWeight: '700'}}>
              Click &amp; Collect <ChevronDown size={14} style={{display: 'inline-block'}} />
            </span>
          </button>
        ) : null} */}
        <div className="brand-wrap">
          <button className="brand-home-btn" onClick={() => navigateTo('/')} aria-label="Go to home page">
            <img className="brand-logo" src={brandLogoUrl(assetBase)} alt={companyDisplayName} />
          </button>
        </div>
        {currentPath === '/' ? (
          <div className="mobile-topbar-actions">
            <button type="button" aria-label="Open search" onClick={() => setIsMobileSearchOpen(true)}>
              <Search size={28} />
            </button>
          </div>
        ) : null}
      </header>
      <section className="hero" style={heroStyle}>
        <button className="hero-arrow hero-arrow-left" aria-label="Previous slide">
          ‹
        </button>
        <button className="hero-arrow hero-arrow-right" aria-label="Next slide">
          ›
        </button>
        <div className="hero-overlay">
          <div className="hero-pickup-card" role="status" aria-live="polite">
            <span className="hero-pickup-store">{companyDisplayName}</span>
            <span className="hero-pickup-sub">
              {isMenuBrowseOnly
                ? 'Viewing menu only — ordering unavailable'
                : orderingSetupStep === 'ready' && selectedOrderingService
                  ? `${selectedOrderingService.name} — ${selectedSlot?.title || 'ASAP'}`
                  : 'Choose service & time to view menu'}
            </span>
            {orderingSetupStep === 'ready' && currentPath === '/' ? (
              <button type="button" className="hero-pickup-change" onClick={resetOrderingFlow}>
                Change
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {currentPath === '/' && orderingSetupStep === 'service' && !isServiceTypesLoading ? (
        <div className="ordering-flow-overlay" role="presentation">
          <div
            className="ordering-flow-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ordering-flow-service-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="ordering-flow-service-title" className="ordering-flow-title">
              How would you like to order?
            </h2>
            <p className="ordering-flow-subtitle">Choose a service below</p>
            {serviceStatusError ? <p className="modal-error">{serviceStatusError}</p> : null}
            <div className="ordering-flow-stack">
              {posServiceTypes.map((svc) => (
                <button
                  key={svc.id}
                  type="button"
                  className="ordering-flow-choice"
                  disabled={isCheckingServiceStatus || isSlotsLoading}
                  onClick={() => void handleSelectPosService(svc)}
                >
                  <span className="ordering-flow-choice-main">{svc.name || `Service ${svc.id}`}</span>
                  {typeof svc.waitingTime === 'number' && svc.waitingTime > 0 ? (
                    <span className="ordering-flow-choice-sub">Est. wait {svc.waitingTime} min</span>
                  ) : null}
                </button>
              ))}
            </div>
            {!posServiceTypes.length ? (
              <p className="ordering-flow-empty">No POS services are configured for this location.</p>
            ) : null}
            <div className="ordering-flow-store-info" aria-label="Store details">
              <p className="ordering-flow-store-info-name">{companyDisplayName}</p>
              {storeContact.address ? (
                <p className="ordering-flow-store-info-line">{storeContact.address}</p>
              ) : null}
              {storeContact.phone ? (
                <p className="ordering-flow-store-info-line">
                  <a className="ordering-flow-store-phone" href={`tel:${storeContact.phone.replace(/\s/g, '')}`}>
                    {storeContact.phone}
                  </a>
                </p>
              ) : null}
              <div className="ordering-flow-store-hours">
                <p className="ordering-flow-store-hours-title">Opening hours</p>
                <ul className="ordering-flow-store-hours-list">
                  {STORE_OPENING_HOURS_LINES.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                className="ordering-flow-view-menu-link"
                disabled={isMenuLoading}
                onClick={() => void handleViewMenuOnly()}
              >
                View menu
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {currentPath === '/' && orderingSetupStep === 'slot' ? (
        <div className="ordering-flow-overlay" role="presentation">
          <div
            className="ordering-flow-dialog ordering-flow-dialog-slot"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ordering-flow-slot-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="ordering-flow-slot-title" className="ordering-flow-title">
              Pick a time
            </h2>
            <p className="ordering-flow-subtitle">
              {selectedOrderingService?.name ? `Service: ${selectedOrderingService.name}` : null}
            </p>
            {slotsLoadError ? <p className="modal-error">{slotsLoadError}</p> : null}
            {!isSlotsLoading && !timeSlots.length ? (
              <p className="modal-error">No collection times are available right now. Please try again later.</p>
            ) : null}
            {!isSlotsLoading && (!timeSlots.length || slotsLoadError) && selectedOrderingService ? (
              <button
                type="button"
                className="ordering-flow-secondary"
                disabled={isMenuLoading}
                onClick={() => void loadMenuForService(selectedOrderingService, null)}
              >
                View Menu
              </button>
            ) : null}
            {isSlotsLoading ? (
              <p className="ordering-flow-loading">Loading available slots…</p>
            ) : (
              <div className="ordering-flow-slot-scroll">
                <div className="ordering-flow-stack">
                  {timeSlots.map((slot, idx) => {
                      const isSelected =
                        pendingSlot &&
                        pendingSlot.title === slot.title &&
                        String(pendingSlot.dateTime ?? '') === String(slot.dateTime ?? '');
                      return (
                        <button
                          key={`${slot.title}-${idx}`}
                          type="button"
                          className={`ordering-flow-slot ${isSelected ? 'ordering-flow-slot--selected' : ''}`}
                          disabled={isMenuLoading}
                          onClick={() => setPendingSlot(slot)}
                        >
                          {slot.title || 'Slot'}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
            <button
              type="button"
              className="ordering-flow-primary"
              disabled={isMenuLoading || isSlotsLoading || !pendingSlot}
              onClick={() => void handleConfirmSlot(pendingSlot)}
            >
              Continue
            </button>
            <button
              type="button"
              className="ordering-flow-secondary"
              disabled={isMenuLoading || !isStoreOpenNow()}
              onClick={() => void skipSlotAndContinue()}
            >
              Collect ASAP
            </button>
            <button type="button" className="ordering-flow-secondary" disabled={isMenuLoading || isSlotsLoading} onClick={handleGoBackFromSlot}>
              Back
            </button>
          </div>
        </div>
      ) : null}

      {currentPath !== '/add-loyalty-wallet' &&
      currentPath !== '/favourite-orders' &&
      currentPath !== '/past-orders' &&
      currentPath !== '/loyalty-history' &&
      currentPath !== '/profile/personal-details' &&
      currentPath !== '/profile/change-password' &&
      currentPath !== '/profile/delete-account' ? (
        <>
          {orderingSetupStep === 'ready' && isMenuBrowseOnly && currentPath === '/' ? (
            <p className="menu-browse-only-banner" role="status">
              You are browsing the menu only.{' '}
              <button type="button" className="menu-browse-only-banner-link" onClick={resetOrderingFlow}>
                Choose a service and time
              </button>{' '}
              to add items and checkout.
            </p>
          ) : null}

          {orderingSetupStep === 'ready' && categories.length > 0 && !(currentPath === '/' && isMenuLoading) ? (
            <nav className="category-strip">
              <button className="cat-arrow left" aria-label="Scroll categories left" onClick={() => scrollCategories('left')}>
                ‹
              </button>
              <div className="category-scroll" ref={categoryScrollRef}>
                {categories.map((category) => (
                  <button
                    key={category.name}
                    className={`cat-tab ${activeCategory === category.name ? 'active' : ''}`}
                    onClick={() => onCategoryClick(category.name)}
                    ref={(element) => {
                      if (element) {
                        categoryButtonRefs.current[category.name] = element;
                      }
                    }}
                  >
                    <span>{category.name}</span>
                    {category.schedule ? <small>{category.schedule}</small> : null}
                  </button>
                ))}
              </div>
              <button className="cat-arrow right" aria-label="Scroll categories right" onClick={() => scrollCategories('right')}>
                ›
              </button>
            </nav>
          ) : null}

          <main className="content">
            {currentPath === '/' && isServiceTypesLoading ? (
              <p className="section-subtitle">Loading services…</p>
            ) : null}
            {orderingSetupStep === 'ready' && currentPath === '/' && isMenuLoading ? (
              <p className="section-subtitle">Loading menu…</p>
            ) : null}
            {orderingSetupStep === 'ready' && productsError ? <p className="section-subtitle">{productsError}</p> : null}
            {(orderingSetupStep === 'ready' && !(currentPath === '/' && isMenuLoading)) || currentPath !== '/' ? (
              filteredSections.map((section) => (
                <section
                  key={section.title}
                  className="menu-section"
                  ref={(element) => {
                    if (element) {
                      sectionRefs.current[section.title] = element;
                    }
                  }}
                  data-section-title={section.title}
                  style={{ scrollMarginTop: '120px' }}
                >
                  <h2>{section.title}</h2>
                  {section.subtitle ? <p className="section-subtitle">{section.subtitle}</p> : null}
                  <div className="product-grid">
                    {section.products.map((product) => (
                      <article className="product-card" key={`${product.id}-${product.name}`} onClick={() => openProductModal(product)}>
                        <div className="product-image">
                          <img
                            src={product.imageUrl || product.base64ImageUrl || INLINE_PLACEHOLDER_IMAGE}
                            alt={product.name}
                            className="product-photo"
                            onError={withDefaultProductImage}
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                          />
                        </div>
                        <div>
                          <h3>{product.name}</h3>
                          <div className="price-row">
                            <strong>{product.price}</strong>
                            <span>{product.kcal}</span>
                          </div>
                          <p>{product.description}</p>
                          {product.availability ? <small className="availability">{product.availability}</small> : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))
            ) : null}
          </main>
        </>
      ) : currentPath === '/favourite-orders' ? (
        <main className="fav-orders-page">
          <div className="fav-orders-shell">
            <h2>Favourite Orders</h2>
          </div>
        </main>
      ) : currentPath === '/past-orders' ? (
        <main className="past-orders-page">
          <div className="past-orders-shell">
            <div className="past-orders-head">
              <h2>Past Orders</h2>
              <div className="past-orders-filter-wrap">
                <button type="button" className="past-orders-filter-btn" onClick={() => setIsPastOrdersFilterOpen((prev) => !prev)}>
                  <SlidersHorizontal size={18} />
                  Filters
                </button>
                {isPastOrdersFilterOpen ? (
                  <div className="past-orders-filter-card">
                    <div className="past-orders-filter-title-row">
                      <h3>Filters</h3>
                      <button
                        type="button"
                        onClick={() => {
                          setPastOrdersAllSales(true);
                          setPastOrdersDate('01/05/2026');
                        }}
                      >
                        Reset Filters
                      </button>
                    </div>
                    <h4>Sale Type</h4>
                    <label className="past-orders-filter-check">
                      <input checked={pastOrdersAllSales} onChange={(event) => setPastOrdersAllSales(event.target.checked)} type="checkbox" />
                      <span>All</span>
                    </label>
                    <h4>Date</h4>
                    <input type="text" value={pastOrdersDate} onChange={(event) => setPastOrdersDate(event.target.value)} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </main>
      ) : currentPath === '/loyalty-history' ? (
        <main className="loyalty-history-page">
          <div className="loyalty-history-shell">
            <h2>Loyalty History</h2>
          </div>
        </main>
      ) : currentPath === '/profile/personal-details' ? (
        <main className="profile-page">
          <div className="profile-shell">
            <div className="profile-panel">
              <h2>Update Profile</h2>
              {!isLoggedIn ? (
                <p className="profile-inline-message">Please sign in to view and update your profile.</p>
              ) : !loggedInCustomerId ? (
                <p className="profile-inline-message">
                  Sign out and sign in again to load your profile. Your saved session does not include an account id.
                </p>
              ) : isProfileLoading ? (
                <p className="profile-inline-message">Loading your details…</p>
              ) : profileLoadError ? (
                <p className="profile-inline-message profile-inline-error">{profileLoadError}</p>
              ) : null}
              {isLoggedIn && loggedInCustomerId && !isProfileLoading && !profileLoadError ? (
              <div className="profile-form">
                <label className="floating-field profile-field">
                  <span>Member No</span>
                  <input readOnly type="text" value={profileMemberNo} />
                </label>
                <label className="floating-field profile-field">
                  <span>WID (If)</span>
                  <input
                    type="text"
                    value={profileWid}
                    onChange={(e) => setProfileWid(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="floating-field profile-field">
                  <span>First Name</span>
                  <input type="text" value={customerFirstName} onChange={(e) => setCustomerFirstName(e.target.value)} />
                </label>
                <label className="floating-field profile-field">
                  <span>Surname</span>
                  <input type="text" value={customerLastName} onChange={(e) => setCustomerLastName(e.target.value)} />
                </label>
                <label className="floating-field profile-field">
                  <span>Email</span>
                  <input readOnly type="email" value={customerEmail} title="Email cannot be changed here." />
                </label>
                <label className="floating-field profile-field">
                  <span>Date of Birth</span>
                  <input
                    type="date"
                    value={profileDob}
                    max={localTodayYmd()}
                    onChange={(e) => setProfileDob(e.target.value)}
                  />
                </label>
                <label className="floating-field profile-field">
                  <span>Gender</span>
                  <select value={profileGender} onChange={(e) => setProfileGender(e.target.value)}>
                    <option value=""> </option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="floating-field profile-field">
                  <span>Phone</span>
                  <input type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                </label>
                {profileSaveError ? <p className="profile-inline-error">{profileSaveError}</p> : null}
                {profileSaveSuccess ? <p className="profile-inline-success">{profileSaveSuccess}</p> : null}
                <button
                  className="profile-btn"
                  type="button"
                  disabled={isProfileSaving}
                  onClick={handleUpdateProfile}
                >
                  {isProfileSaving ? 'Saving…' : 'Update Details'}
                </button>
              </div>
              ) : null}

            </div>
          </div>
        </main>
      ) : currentPath === '/profile/change-password' ? (
        <main className="profile-page">
          <div className="profile-shell">
            <div className="profile-panel profile-password-panel">
              <h2>Change Password</h2>
              {!isLoggedIn || !loggedInCustomerId ? (
                <p className="profile-inline-message">Please sign in to change your password.</p>
              ) : (
              <div className="profile-form">
                <label className="floating-field profile-field">
                  <span>Current Password</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={passwordCurrent}
                    onChange={(e) => setPasswordCurrent(e.target.value)}
                  />
                </label>
                <label className="floating-field profile-field">
                  <span>New Password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={passwordNew}
                    onChange={(e) => setPasswordNew(e.target.value)}
                  />
                </label>
                <label className="floating-field profile-field">
                  <span>Confirm Password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                  />
                </label>
                {changePasswordError ? <p className="profile-inline-error">{changePasswordError}</p> : null}
                {changePasswordSuccess ? <p className="profile-inline-success">{changePasswordSuccess}</p> : null}
                <button
                  className="profile-btn"
                  type="button"
                  disabled={isChangingPassword}
                  onClick={handleChangePassword}
                >
                  {isChangingPassword ? 'Updating…' : 'Update Password'}
                </button>
              </div>
              )}
            </div>
          </div>
        </main>
      ) : currentPath === '/profile/delete-account' ? (
        <main className="profile-page">
          <div className="profile-shell">
            <div className="profile-panel profile-delete-panel">
              <h2>Delete Account</h2>
              <p>Are you sure? If you continue all profile information will be removed and purchases history will not be accessible</p>
              <button className="profile-btn profile-danger-btn" type="button">
                Delete Account
              </button>
            </div>
          </div>
        </main>
      ) : currentPath === '/add-loyalty-wallet' ? (
        <main className="chippy-points-page">
          <div className="chippy-points-shell">
            <h2>CHIPPY POINTS &amp; STAMPS</h2>
            <div className="chippy-rewards-card">
              <h3>Rewards</h3>
              <div className="chippy-rewards-inner">
                <p className="chippy-member-name">Bervan Altun</p>
                <p className="chippy-member-id">TCGA0971637</p>

                <div className="chippy-points-row">
                  <div className="chippy-points-count">
                    <strong>0</strong>
                    <div>
                      Chippy
                      <br />
                      Points
                    </div>
                  </div>
                  <strong className="chippy-points-value">£0.00</strong>
                </div>

                <div className="chippy-progress-track">
                  <span className="chippy-progress-fill" />
                </div>

                <div className="chippy-stamps-section">
                  <div className="chippy-stamps-row">
                    <span className="stamp-dot" />
                    <span className="stamp-dot" />
                    <span className="stamp-dot" />
                    <span className="stamp-dot" />
                    <span className="stamp-badge">Free Chips</span>
                  </div>
                  <p className="chippy-stamps-text">
                    <span className="chippy-stamps-count">0/5</span>&nbsp;&nbsp;You are 5 stamps away from your free portion of chips!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      ) : null}

      {currentPath === '/' && !isMenuBrowseOnly ? (
        <button className="cart-sticky" onClick={() => setIsCartDrawerOpen(true)}>
          <span className="cart-count">{cartSummary.totalItems}</span>
          <span>View Cart</span>
          <strong>{formatPrice(cartSummary.totalAmount)}</strong>
        </button>
      ) : null}

      <div
        className={`cart-drawer-overlay ${isCartDrawerOpen ? 'open' : ''}`}
        onClick={() => setIsCartDrawerOpen(false)}
        role="presentation"
      >
        <aside className="cart-drawer" onClick={(event) => event.stopPropagation()}>
          <button className="cart-drawer-close" onClick={() => setIsCartDrawerOpen(false)} aria-label="Close cart drawer">
            ×
          </button>
          <h3>Cart</h3>
          <p className="cart-drawer-location">
            {selectedOrderingService?.name || 'Order'} — {companyDisplayName}
            {selectedSlot?.title ? ` · ${selectedSlot.title}` : null}
          </p>
          <div className="cart-drawer-items">
            {cartItems.length ? (
              cartItems.map((item) => (
                <article key={item.id} className="cart-drawer-item">
                  <div className="cart-drawer-item-head">
                    <strong>{item.name}</strong>
                    <span>{formatPrice(item.lineTotal)}</span>
                  </div>
                  <button className="cart-details-btn" onClick={() => toggleCartItemDetails(item.id)}>
                    Show details <span>{expandedCartItemIds[item.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                  </button>
                  {expandedCartItemIds[item.id] && item.selectedOptions?.length ? (
                    <p className="cart-item-options">{item.selectedOptions.map((option) => option.name).join(', ')}</p>
                  ) : null}
                  <div className="cart-item-actions">
                    <button type="button" className="cart-action-btn" disabled>
                      Customise
                    </button>
                    <button type="button" className="cart-remove-btn" onClick={() => removeCartItem(item.id)}>
                      Remove
                    </button>
                    <div className="cart-qty-stepper">
                      <button type="button" onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}>
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button type="button" onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}>
                        +
                      </button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <p className="cart-empty">Your cart is empty.</p>
            )}
            <div className="cart-expand-block">
              <button className="cart-expand-trigger" onClick={() => setIsNotesExpanded((prev) => !prev)}>
                <span>Add order notes/cutlery</span>
                <span>{isNotesExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
              </button>
              {isNotesExpanded ? (
                <div className="cart-expand-fields">
                  <textarea
                    value={orderNotes}
                    onChange={(event) => setOrderNotes(event.target.value)}
                    placeholder="Any special instructions for us?"
                    rows={4}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <div className="cart-drawer-footer">
            <div className="cart-total-row">
              <span>Total</span>
              <strong>{formatPrice(cartSummary.totalAmount)}</strong>
            </div>
            <button className="cart-checkout-btn" disabled={!cartItems.length} onClick={openCheckout}>
              Checkout
            </button>
          </div>
        </aside>
      </div>

      {currentPath === '/review-order' ? (
        <ReviewOrderPage
          checkoutOverlayStyle={checkoutOverlayStyle}
          setIsSidebarOpen={setIsSidebarOpen}
          navigateTo={navigateTo}
          assetBase={assetBase}
          storeDisplayName={companyDisplayName}
          isScheduleExpanded={isScheduleExpanded}
          setIsScheduleExpanded={setIsScheduleExpanded}
          checkoutScheduleType={checkoutScheduleType}
          scheduledTime={scheduledTime}
          setCheckoutScheduleType={setCheckoutScheduleType}
          scheduledDate={scheduledDate}
          setScheduledDate={setScheduledDate}
          setScheduledTime={setScheduledTime}
          checkoutSlots={visibleCheckoutSlots}
          isCheckoutSlotsLoading={isCheckoutSlotsLoading}
          checkoutSlotsError={checkoutSlotsError}
          checkoutPaymentMethod={checkoutPaymentMethod}
          setCheckoutPaymentMethod={setCheckoutPaymentMethod}
          saveCard={saveCard}
          setSaveCard={setSaveCard}
          formatPrice={formatPrice}
          cartSummary={cartSummary}
          canPlaceOrder={canPlaceOrder}
          customerFirstName={customerFirstName}
          setCustomerFirstName={setCustomerFirstName}
          customerLastName={customerLastName}
          setCustomerLastName={setCustomerLastName}
          customerPhone={customerPhone}
          setCustomerPhone={setCustomerPhone}
          customerEmail={customerEmail}
          setCustomerEmail={setCustomerEmail}
          onPlaceOrder={placeOrder}
          isPlacingOrder={isPlacingOrder}
          placeOrderError={placeOrderError}
          placeOrderSuccess={placeOrderSuccess}
          stripePublishableKey={runtimeSettings.stripePublishableKey}
          stripeConnectAccountId={runtimeSettings.stripeConnectAccountId}
          platformFeeAmount={runtimeSettings.platformFeeAmount}
          stripeTestChargeAmount={runtimeSettings.stripeTestChargeAmount}
          paymentMethods={companyPaymentMethods}
          isPaymentMethodsLoading={isPaymentMethodsLoading}
          onCreateStripePaymentIntent={createStripePaymentIntent}
        />
      ) : null}

      {isOrderSuccessModalOpen ? (
        <div className="order-success-modal-overlay" onClick={handleCloseOrderSuccessModal} role="presentation">
          <div className="order-success-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Order Placed</h3>
            <p>Order has been placed successfully.</p>
            <button type="button" className="ordering-flow-primary" onClick={handleCloseOrderSuccessModal}>
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {isSubMenuPickerOpen ? (
        <div className="submenu-modal-overlay" onClick={closeSubMenuPicker} role="presentation">
          <div className="submenu-modal" role="dialog" aria-modal="true" aria-labelledby="submenu-modal-title" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="product-modal-close" onClick={closeSubMenuPicker} aria-label="Close submenu">
              ×
            </button>
            <h3 id="submenu-modal-title" className="ordering-setup-title">
              Choose an option
            </h3>
            {subMenuParentProduct ? <p className="ordering-setup-hint">{subMenuParentProduct.name}</p> : null}
            {isSubMenuPickerLoading ? <p className="section-subtitle">Loading options…</p> : null}
            {subMenuPickerError ? <p className="modal-error">{subMenuPickerError}</p> : null}
            <div className="submenu-option-list">
              {subMenuPickerItems.map((row) => (
                <button key={row.id} type="button" className="ordering-service-btn submenu-option-btn" onClick={() => onPickSubMenuItem(row)}>
                  <span className="ordering-service-name">{row.name}</span>
                  <span className="ordering-service-meta">{formatPrice(toNumber(row.salePrice))}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {isProductModalOpen && selectedProduct ? (
        <div className="product-modal-overlay" onClick={closeProductModal} role="presentation">
          <div
            className={`product-modal${selectedProduct.isMealDealBundle ? ' product-modal--meal-deal' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button className="product-modal-close" onClick={closeProductModal} aria-label="Close product modal">
              ×
            </button>
            {isProductDetailLoading ? (
              <div className="product-modal-loading" role="status" aria-live="polite">
                Loading options…
              </div>
            ) : null}
            <div className="product-modal-content">
              <div className="product-modal-image-wrap">
                <img
                  src={selectedProduct.imageUrl || selectedProduct.base64ImageUrl || INLINE_PLACEHOLDER_IMAGE}
                  onError={(event) => {
                    event.currentTarget.src = INLINE_PLACEHOLDER_IMAGE;
                  }}
                  alt={selectedProduct.name}
                  className="product-modal-image"
                />
              </div>
              <div className="product-modal-details">
                <div className="product-modal-scroll">
                  <h3>{selectedProduct.name}</h3>
                  <div className="product-modal-price-row">
                    <strong className='product-modal-price'>{selectedProduct.price}</strong>
                    {selectedProduct.kcal ? <span>{selectedProduct.kcal}</span> : null}
                  </div>
                  {selectedProduct.description ? <p>{selectedProduct.description}</p> : null}
                  {selectedProduct.isMealDealBundle &&
                  !(bundleSlotPlan.length > 0 && bundleFlowPhase !== 'standard') ? (
                    <p className="product-modal-bundle-hint">
                      Bundle: pick each required step. Extra options (for example chips) appear under your choice when
                      you select it.
                    </p>
                  ) : null}

                  {showProductBundleWizard && bundleFlowPhase === 'overview' ? (
                    <div className="bundle-flow">
                      <h4 className="bundle-flow-subtitle">Choose Your Sausage &amp; Chips</h4>
                      <div className="bundle-slot-list">
                        {bundleSlotPlan.map((slot, idx) => {
                          const sg = slot.sausageGroup;
                          const chosenId = readSelectedModifierIds(selectedModifiersByGroup, sg.id)[0];
                          const chosenItem = chosenId ? sg.items.find((it) => it.id === chosenId) : null;
                          const done = Boolean(chosenId);
                          return (
                            <button
                              key={sg.id}
                              type="button"
                              className={`bundle-slot-card${done ? ' bundle-slot-card--done' : ''}`}
                              onClick={() => {
                                setBundleActiveSlotIndex(idx);
                                setBundleFlowPhase(done ? 'slotExtras' : 'pickSausage');
                              }}
                            >
                              <span className="bundle-slot-num" aria-hidden>
                                {idx + 1}
                              </span>
                              <span className="bundle-slot-main">
                                <span className="bundle-slot-label">Choose Your Sausage &amp; Chips</span>
                                {chosenItem ? (
                                  <span className="bundle-slot-value">{chosenItem.name}</span>
                                ) : (
                                  <span className="bundle-slot-placeholder">Tap to choose</span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {bundleRemainingModifierGroups.length ? (
                        <div className="bundle-flow-more">
                          <h4 className="bundle-flow-subtitle">More options</h4>
                          {bundleRemainingModifierGroups.map((group) => {
                            const groupKey = String(group.id);
                            const isAccordionExpanded = modifierAccordionOpenByGroupId[groupKey] !== false;
                            const maxSel = maxSelectionForGroup(group);
                            const headingId = `modifier-group-heading-${groupKey}`;
                            const panelId = `modifier-group-options-${groupKey}`;
                            return (
                              <div key={group.id} className="modifier-group">
                                <button
                                  type="button"
                                  className="modifier-group-header"
                                  aria-expanded={isAccordionExpanded}
                                  aria-controls={panelId}
                                  id={headingId}
                                  onClick={() => toggleModifierAccordion(group.id)}
                                >
                                  <div className="modifier-group-title-wrap">
                                    <strong>{group.name}</strong>
                                    <span className="modifier-group-toggle" aria-hidden>
                                      {isAccordionExpanded ? (
                                        <ChevronDown className="modifier-group-toggle-chevron" size={16} strokeWidth={2.5} />
                                      ) : (
                                        <ChevronUp className="modifier-group-toggle-chevron" size={16} strokeWidth={2.5} />
                                      )}
                                    </span>
                                  </div>
                                  <small>{group.required ? 'Required' : 'Optional'}</small>
                                </button>
                                {isAccordionExpanded ? (
                                  <>
                                    <p className="modifier-group-hint">Choose up to {maxSel}</p>
                                    <div className="modifier-options" id={panelId} role="group" aria-labelledby={headingId}>
                                      {group.items.map((item) => (
                                        <div key={item.id} className="modifier-option-stack">
                                          <label className="modifier-option">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(
                                                readSelectedModifierIds(selectedModifiersByGroup, group.id).includes(
                                                  item.id
                                                )
                                              )}
                                              onChange={() => onSelectModifierItem(group.id, item.id)}
                                            />
                                            <span className="modifier-radio" aria-hidden="true" />
                                            <span>{item.name}</span>
                                            {item.price > 0 ? <em>{`+ ${formatPrice(item.price)}`}</em> : null}
                                          </label>
                                          {Boolean(item.childGroups?.length) &&
                                          readSelectedModifierIds(selectedModifiersByGroup, group.id).includes(
                                            item.id
                                          ) ? (
                                            <div className="modifier-nested">
                                              {item.childGroups.map((cg) => {
                                                const nestedKey = nestedModifierStateKey(group.id, item.id, cg.id);
                                                const nestedMax = maxSelectionForGroup(cg);
                                                return (
                                                  <div key={nestedKey} className="modifier-nested-group">
                                                    <div className="modifier-nested-head">
                                                      <strong>{cg.name}</strong>
                                                      <small>
                                                        {cg.required ? 'Required' : 'Optional'} · up to {nestedMax}
                                                      </small>
                                                    </div>
                                                    <div className="modifier-options modifier-options-nested" role="group">
                                                      {cg.items.map((opt) => (
                                                        <label key={opt.id} className="modifier-option modifier-option-nested">
                                                          <input
                                                            type="checkbox"
                                                            checked={Boolean(
                                                              readSelectedModifierIds(
                                                                selectedModifiersByGroup,
                                                                nestedKey
                                                              ).includes(opt.id)
                                                            )}
                                                            onChange={() =>
                                                              onSelectNestedModifierItem(group.id, item.id, cg.id, opt.id)
                                                            }
                                                          />
                                                          <span className="modifier-radio" aria-hidden="true" />
                                                          <span>{opt.name}</span>
                                                          {opt.price > 0 ? (
                                                            <em>{`+ ${formatPrice(opt.price)}`}</em>
                                                          ) : null}
                                                        </label>
                                                      ))}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {showProductBundleWizard && bundleFlowPhase === 'pickSausage' ? (
                    <div className="bundle-flow bundle-flow-pick">
                      <button
                        type="button"
                        className="bundle-flow-back"
                        onClick={() => setBundleFlowPhase('overview')}
                      >
                        ← Back
                      </button>
                      <h4 className="bundle-flow-title">Choose Your Sausage &amp; Chips</h4>
                      <div className="bundle-choice-grid">
                        {(bundleSlotPlan[bundleActiveSlotIndex]?.sausageGroup?.items || []).map((item) => {
                          const sg = bundleSlotPlan[bundleActiveSlotIndex].sausageGroup;
                          const selected = readSelectedModifierIds(selectedModifiersByGroup, sg.id).includes(item.id);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              className={`bundle-choice-card${selected ? ' bundle-choice-card--selected' : ''}`}
                              onClick={() => {
                                if (!selected) {
                                  onSelectModifierItem(sg.id, item.id);
                                }
                                setBundleFlowPhase('slotExtras');
                              }}
                            >
                              <div className="bundle-choice-image-wrap">
                                <img
                                  src={item.imageUrl || INLINE_PLACEHOLDER_IMAGE}
                                  alt=""
                                  className="bundle-choice-image"
                                  onError={(e) => {
                                    e.currentTarget.src = INLINE_PLACEHOLDER_IMAGE;
                                  }}
                                />
                              </div>
                              <span className="bundle-choice-name">{item.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {showProductBundleWizard && bundleFlowPhase === 'slotExtras' ? (
                    <div className="bundle-flow bundle-flow-extras">
                      <button
                        type="button"
                        className="bundle-flow-back"
                        onClick={() => setBundleFlowPhase('pickSausage')}
                      >
                        ← Back
                      </button>
                      {(() => {
                        const slot = bundleSlotPlan[bundleActiveSlotIndex];
                        const sg = slot?.sausageGroup;
                        const chosenId = sg ? readSelectedModifierIds(selectedModifiersByGroup, sg.id)[0] : null;
                        const chosenItem = chosenId && sg ? sg.items.find((it) => it.id === chosenId) : null;
                        return chosenItem ? (
                          <>
                            <h4 className="bundle-flow-title">{chosenItem.name}</h4>
                            {chosenItem.description ? (
                              <p className="bundle-flow-desc">{chosenItem.description}</p>
                            ) : null}
                          </>
                        ) : null;
                      })()}
                      {(() => {
                        const slot = bundleSlotPlan[bundleActiveSlotIndex];
                        const sg = slot?.sausageGroup;
                        const chosenId = sg ? readSelectedModifierIds(selectedModifiersByGroup, sg.id)[0] : null;
                        const chosenItem = chosenId && sg ? sg.items.find((it) => it.id === chosenId) : null;
                        if (!chosenItem?.childGroups?.length) return null;
                        return chosenItem.childGroups.map((cg) => {
                          const nestedKey = nestedModifierStateKey(sg.id, chosenItem.id, cg.id);
                          const nestedMax = maxSelectionForGroup(cg);
                          return (
                            <div key={nestedKey} className="modifier-group bundle-flow-mod-group">
                              <div className="modifier-group-header modifier-group-header-static">
                                <div className="modifier-group-title-wrap">
                                  <strong>{cg.name}</strong>
                                </div>
                                <small>{cg.required ? 'Required' : 'Optional'}</small>
                              </div>
                              <p className="modifier-group-hint">Choose up to {nestedMax}</p>
                              <div className="modifier-options" role="group">
                                {cg.items.map((opt) => (
                                  <label key={opt.id} className="modifier-option">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(
                                        readSelectedModifierIds(selectedModifiersByGroup, nestedKey).includes(opt.id)
                                      )}
                                      onChange={() =>
                                        onSelectNestedModifierItem(sg.id, chosenItem.id, cg.id, opt.id)
                                      }
                                    />
                                    <span className="modifier-radio" aria-hidden="true" />
                                    <span>{opt.name}</span>
                                    {opt.price > 0 ? <em>{`+ ${formatPrice(opt.price)}`}</em> : null}
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                      {(() => {
                        const slot = bundleSlotPlan[bundleActiveSlotIndex];
                        const sea = slot?.seasoningGroup;
                        if (!sea) return null;
                        const sk = bundleSeasoningStateKey(bundleActiveSlotIndex, sea.id);
                        const maxSel = maxSelectionForGroup(sea);
                        return (
                          <div key={sk} className="modifier-group bundle-flow-mod-group">
                            <div className="modifier-group-header modifier-group-header-static">
                              <div className="modifier-group-title-wrap">
                                <strong>{sea.name}</strong>
                              </div>
                              <small>{sea.required ? 'Required' : 'Optional'}</small>
                            </div>
                            <p className="modifier-group-hint">Choose up to {maxSel}</p>
                            <div className="modifier-options" role="group">
                              {sea.items.map((opt) => (
                                <label key={opt.id} className="modifier-option">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(readSelectedModifierIds(selectedModifiersByGroup, sk).includes(opt.id))}
                                    onChange={() =>
                                      onSelectBundleSeasoning(bundleActiveSlotIndex, sea.id, opt.id)
                                    }
                                  />
                                  <span className="modifier-radio" aria-hidden="true" />
                                  <span>{opt.name}</span>
                                  {opt.price > 0 ? <em>{`+ ${formatPrice(opt.price)}`}</em> : null}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      <button
                        type="button"
                        className="bundle-flow-done ordering-flow-primary"
                        onClick={() => {
                          const slot = bundleSlotPlan[bundleActiveSlotIndex];
                          if (!slot) return;
                          const sausage = slot.sausageGroup;
                          const chosenId = readSelectedModifierIds(selectedModifiersByGroup, sausage.id)[0];
                          if (!chosenId) {
                            setModalError('Please choose a sausage option.');
                            return;
                          }
                          const item = sausage.items.find((it) => it.id === chosenId);
                          if (item?.childGroups?.length) {
                            for (const cg of item.childGroups) {
                              const sk = nestedModifierStateKey(sausage.id, item.id, cg.id);
                              const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
                              const minimum = Math.max(cg.required ? 1 : 0, Number(cg.minSelection || 0));
                              if (sel.length < minimum) {
                                setModalError(`Please select an option for ${cg.name}.`);
                                return;
                              }
                            }
                          }
                          if (slot.seasoningGroup) {
                            const sk = bundleSeasoningStateKey(bundleActiveSlotIndex, slot.seasoningGroup.id);
                            const sel = readSelectedModifierIds(selectedModifiersByGroup, sk);
                            const minimum = Math.max(
                              slot.seasoningGroup.required ? 1 : 0,
                              Number(slot.seasoningGroup.minSelection || 0)
                            );
                            if (sel.length < minimum) {
                              setModalError(`Please select an option for ${slot.seasoningGroup.name}.`);
                              return;
                            }
                          }
                          setModalError('');
                          setBundleFlowPhase('overview');
                        }}
                      >
                        Save &amp; continue
                      </button>
                    </div>
                  ) : null}

                  {!showProductBundleWizard && activeProductModifierGroups.length ? (
                    activeProductModifierGroups.map((group) => {
                      const groupKey = String(group.id);
                      const isAccordionExpanded = modifierAccordionOpenByGroupId[groupKey] !== false;
                      const maxSel = maxSelectionForGroup(group);
                      const headingId = `modifier-group-heading-${groupKey}`;
                      const panelId = `modifier-group-options-${groupKey}`;
                      return (
                        <div key={group.id} className="modifier-group">
                          <button
                            type="button"
                            className="modifier-group-header"
                            aria-expanded={isAccordionExpanded}
                            aria-controls={panelId}
                            id={headingId}
                            onClick={() => toggleModifierAccordion(group.id)}
                          >
                            <div className="modifier-group-title-wrap">
                              <strong>{group.name}</strong>
                              <span className="modifier-group-toggle" aria-hidden>
                                {isAccordionExpanded ? (
                                  <ChevronDown className="modifier-group-toggle-chevron" size={16} strokeWidth={2.5} />
                                ) : (
                                  <ChevronUp className="modifier-group-toggle-chevron" size={16} strokeWidth={2.5} />
                                )}
                              </span>
                            </div>
                            <small>{group.required ? 'Required' : 'Optional'}</small>
                          </button>
                          {isAccordionExpanded ? (
                            <>
                              <p className="modifier-group-hint">Choose up to {maxSel}</p>
                              <div className="modifier-options" id={panelId} role="group" aria-labelledby={headingId}>
                                {group.items.map((item) => (
                                  <div key={item.id} className="modifier-option-stack">
                                    <label className="modifier-option">
                                    <input
                                      type="checkbox"
                                        checked={Boolean(
                                          readSelectedModifierIds(selectedModifiersByGroup, group.id).includes(item.id)
                                        )}
                                      onChange={() => onSelectModifierItem(group.id, item.id)}
                                    />
                                    <span className="modifier-radio" aria-hidden="true" />
                                    <span>{item.name}</span>
                                    {item.price > 0 ? <em>{`+ ${formatPrice(item.price)}`}</em> : null}
                                    </label>
                                    {Boolean(item.childGroups?.length) &&
                                    readSelectedModifierIds(selectedModifiersByGroup, group.id).includes(item.id) ? (
                                      <div className="modifier-nested">
                                        {item.childGroups.map((cg) => {
                                          const nestedKey = nestedModifierStateKey(group.id, item.id, cg.id);
                                          const nestedMax = maxSelectionForGroup(cg);
                                          return (
                                            <div key={nestedKey} className="modifier-nested-group">
                                              <div className="modifier-nested-head">
                                                <strong>{cg.name}</strong>
                                                <small>
                                                  {cg.required ? 'Required' : 'Optional'} · up to {nestedMax}
                                                </small>
                                              </div>
                                              <div className="modifier-options modifier-options-nested" role="group">
                                                {cg.items.map((opt) => (
                                                  <label key={opt.id} className="modifier-option modifier-option-nested">
                                                    <input
                                                      type="checkbox"
                                                      checked={Boolean(
                                                        readSelectedModifierIds(
                                                          selectedModifiersByGroup,
                                                          nestedKey
                                                        ).includes(opt.id)
                                                      )}
                                                      onChange={() =>
                                                        onSelectNestedModifierItem(group.id, item.id, cg.id, opt.id)
                                                      }
                                                    />
                                                    <span className="modifier-radio" aria-hidden="true" />
                                                    <span>{opt.name}</span>
                                                    {opt.price > 0 ? <em>{`+ ${formatPrice(opt.price)}`}</em> : null}
                                  </label>
                                                ))}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </div>
                      );
                    })
                  ) : null}

                  {modalError ? <p className="modal-error">{modalError}</p> : null}
                </div>
                <div className="product-modal-footer">
                  {!isMenuBrowseOnly ? (
                    <div className="qty-control">
                      <button
                        type="button"
                        disabled={isProductDetailLoading}
                        onClick={() => setModalQuantity((qty) => Math.max(1, qty - 1))}
                      >
                        −
                      </button>
                      <span>{modalQuantity}</span>
                      <button type="button" disabled={isProductDetailLoading} onClick={() => setModalQuantity((qty) => qty + 1)}>
                        +
                      </button>
                    </div>
                  ) : null}
                  {isMenuBrowseOnly ? (
                    <p className="product-modal-browse-only-note">
                      Ordering is unavailable while viewing the menu only.
                    </p>
                  ) : (
                    <button
                      className="add-to-cart-btn"
                      disabled={
                        isProductDetailLoading ||
                        !canAddProductWithCurrentModifiers ||
                        (showProductBundleWizard && bundleFlowPhase !== 'overview')
                      }
                      onClick={addToCart}
                    >
                      Add To Cart - {formatPrice(((selectedProduct.basePrice || 0) + selectedModifierAmount) * modalQuantity)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
        role="presentation"
      >
        <aside className="sidebar" onClick={(event) => event.stopPropagation()}>
          <button className="sidebar-close" onClick={() => setIsSidebarOpen(false)} aria-label="Close menu">
            <X size={44} />
          </button>

          {isLoggedIn ? <div className="sidebar-user">{currentCustomerName || 'Logged In Customer'}</div> : null}
          <button className="sidebar-item sidebar-order" onClick={() => onSidebarItemClick('ORDER NOW')}>
            ORDER NOW
          </button>
          {sidebarItems.map((item) => (
            <div key={item} style={{ position: 'relative' }}>
              {item === 'PROFILE' && shouldShowProfileSubmenu ? <div className="sidebar-active-line"></div> : null}
              <button
                className={`sidebar-item ${isSidebarItemActive(item) ? 'sidebar-item-active' : ''} ${item === 'PROFILE' ? `sidebar-profile-trigger ${isProfileSubmenuRoute ? 'active' : ''}` : ''}`}
                onClick={() => onSidebarItemClick(item)}
              >
                {item}
              </button>
              {item === 'PROFILE' && shouldShowProfileSubmenu ? (
                <div className="sidebar-submenu">
                  <button
                    className={`sidebar-subitem ${currentPath === '/profile/personal-details' ? 'active' : ''}`}
                    onClick={goToProfilePersonalDetails}
                  >
                    PERSONAL DETAILS
                  </button>
                  <button
                    className={`sidebar-subitem ${currentPath === '/profile/change-password' ? 'active' : ''}`}
                    onClick={goToProfileChangePassword}
                  >
                    CHANGE PASSWORD
                  </button>
                  <button
                    className={`sidebar-subitem ${currentPath === '/profile/delete-account' ? 'active' : ''}`}
                    onClick={goToProfileDeleteAccount}
                  >
                    DELETE ACCOUNT
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </aside>
      </div>

      {currentPath === '/' && isMobileSearchOpen ? (
        <div className="mobile-sheet-overlay" onClick={() => setIsMobileSearchOpen(false)} role="presentation">
          <div className="mobile-search-sheet" onClick={(event) => event.stopPropagation()}>
            <button className="mobile-sheet-close" type="button" onClick={() => setIsMobileSearchOpen(false)} aria-label="Close search">
              ×
            </button>
            <div className="mobile-search-input-wrap">
              <Search size={18} />
              <input
                type="text"
                value={mobileSearchQuery}
                onChange={(event) => setMobileSearchQuery(event.target.value)}
                aria-label="Search products"
              />
            </div>
            {mobileSearchQuery.trim() && mobileSearchSections.length ? (
              <div className="mobile-search-results">
                {mobileSearchSections.map((section) => (
                  <section key={section.title} className="mobile-search-section">
                    <h4>{section.title}</h4>
                    {section.products.map((product, index) => (
                      <article
                        key={`${section.title}-${product.name}-${index}`}
                        className="mobile-search-item"
                        onClick={() => {
                          openProductModal(product);
                          setIsMobileSearchOpen(false);
                        }}
                      >
                        <div className="mobile-search-item-image">
                          <img
                            src={product.imageUrl || product.base64ImageUrl || INLINE_PLACEHOLDER_IMAGE}
                            alt={product.name}
                            onError={withDefaultProductImage}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div className="mobile-search-item-body">
                          <h5>{product.name}</h5>
                          <strong>{product.price}</strong>
                          <p>{product.description}</p>
                        </div>
                      </article>
                    ))}
                  </section>
                ))}
              </div>
            ) : mobileSearchQuery.trim() ? (
              <p className="mobile-search-empty">We couldn&apos;t find any matches</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {isSignInModalOpen ? (
        <div className="auth-overlay">
          <div className="auth-modal sign-in-modal">
            <button
              type="button"
              className="auth-close"
              onClick={() => {
                setIsSignInModalOpen(false);
                setSignInError('');
                setSignInNotice('');
              }}
              aria-label="Close sign in modal"
            >
              ×
            </button>

            <div className="auth-form">
              <label className="floating-field">
                <span>Email *</span>
                <input type="email" value={signInEmail} onChange={(event) => setSignInEmail(event.target.value)} />
              </label>

              <label className="floating-field">
                <span>Password *</span>
                <input type="password" value={signInPassword} onChange={(event) => setSignInPassword(event.target.value)} />
              </label>

              <label className="remember-row">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>

              <button
                type="button"
                className="auth-link-btn"
                onClick={() => {
                  resetForgotPasswordModal();
                  setForgotPasswordEmail(signInEmail.trim());
                  setIsSignInModalOpen(false);
                  setSignInError('');
                  setSignInNotice('');
                  setIsForgotPasswordModalOpen(true);
                }}
              >
                Forgot Password?
              </button>

              <button className="auth-submit" disabled={!signInEmail.trim() || !signInPassword.trim() || isSigningIn} onClick={handleSignIn}>
                {isSigningIn ? 'Signing In...' : 'Sign In'}
              </button>
              {signInError ? <p className="auth-status-message auth-status-error">{signInError}</p> : null}
              {signInNotice ? <p className="auth-status-message auth-status-success">{signInNotice}</p> : null}
              {signUpSuccess ? <p className="auth-status-message auth-status-success">{signUpSuccess}</p> : null}

              <p className="auth-switch">
                Not a member?{' '}
                <button
                  className="auth-link-btn inline"
                  onClick={() => {
                    setIsSignInModalOpen(false);
                    setSignInError('');
                    setSignUpError('');
                    setIsSignUpModalOpen(true);
                  }}
                >
                  Sign Up
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {isForgotPasswordModalOpen ? (
        <div className="auth-overlay">
          <div className="auth-modal forgot-password-modal">
            <button
              type="button"
              className="auth-close auth-close--start"
              onClick={() => {
                setIsForgotPasswordModalOpen(false);
                resetForgotPasswordModal();
              }}
              aria-label="Close reset password modal"
            >
              ×
            </button>

            <div className="auth-forgot-heading">
              <h2>{forgotPasswordStep === 'email' ? 'Reset Your Password' : 'Create new password'}</h2>
            </div>

            <div className="auth-form auth-form--forgot">
              {forgotPasswordStep === 'email' ? (
                <>
                  <label className="floating-field">
                    <span>Email *</span>
                    <input
                      type="email"
                      autoComplete="email"
                      value={forgotPasswordEmail}
                      onChange={(event) => setForgotPasswordEmail(event.target.value)}
                    />
                  </label>

                  <button
                    type="button"
                    className="auth-submit auth-submit--tan"
                    disabled={!forgotPasswordEmail.trim() || isSendingForgotPassword}
                    onClick={handleForgotPasswordRequest}
                  >
                    {isSendingForgotPassword ? 'Submitting…' : 'Submit'}
                  </button>
                </>
              ) : (
                <>
                  <p className="auth-forgot-email-hint">
                    Reset code sent to <strong>{forgotPasswordEmail.trim()}</strong>
                  </p>
                  {forgotPasswordSuccess ? (
                    <p className="auth-status-message auth-status-success">{forgotPasswordSuccess}</p>
                  ) : null}
                  <label className="floating-field">
                    <span>Reset code *</span>
                    <input
                      type="text"
                      autoComplete="one-time-code"
                      value={forgotResetCode}
                      onChange={(event) => setForgotResetCode(event.target.value)}
                    />
                  </label>
                  <label className="floating-field">
                    <span>New password *</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={forgotNewPassword}
                      onChange={(event) => setForgotNewPassword(event.target.value)}
                    />
                  </label>
                  <label className="floating-field">
                    <span>Confirm password *</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={forgotConfirmPassword}
                      onChange={(event) => setForgotConfirmPassword(event.target.value)}
                    />
                  </label>

                  <button
                    type="button"
                    className="auth-submit auth-submit--tan"
                    disabled={
                      !forgotResetCode.trim() ||
                      !forgotNewPassword.trim() ||
                      !forgotConfirmPassword.trim() ||
                      isCompletingPasswordReset
                    }
                    onClick={handleForgotPasswordComplete}
                  >
                    {isCompletingPasswordReset ? 'Resetting…' : 'Reset password'}
                  </button>

                  <button
                    type="button"
                    className="auth-link-btn auth-forgot-back"
                    onClick={() => {
                      setForgotPasswordStep('email');
                      setForgotResetCode('');
                      setForgotNewPassword('');
                      setForgotConfirmPassword('');
                      setForgotPasswordError('');
                      setForgotPasswordSuccess('');
                    }}
                  >
                    Use a different email
                  </button>
                </>
              )}

              {forgotPasswordError ? <p className="auth-status-message auth-status-error">{forgotPasswordError}</p> : null}
              {forgotPasswordStep === 'email' ? (
                <>
                  <p className="auth-switch">
                    Already a member?{' '}
                    <button
                      type="button"
                      className="auth-link-btn inline"
                      onClick={() => {
                        const email = forgotPasswordEmail.trim();
                        setIsForgotPasswordModalOpen(false);
                        resetForgotPasswordModal();
                        setSignInEmail(email);
                        setSignInError('');
                        setSignInNotice('');
                        setIsSignInModalOpen(true);
                      }}
                    >
                      Sign In
                    </button>
                  </p>
                  <p className="auth-switch auth-switch--tight">
                    Not a member?{' '}
                    <button
                      type="button"
                      className="auth-link-btn inline"
                      onClick={() => {
                        const email = forgotPasswordEmail.trim();
                        setIsForgotPasswordModalOpen(false);
                        resetForgotPasswordModal();
                        if (email) setSignUpEmail(email);
                        setSignUpError('');
                        setIsSignUpModalOpen(true);
                      }}
                    >
                      Sign Up
                    </button>
                  </p>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {isSignUpModalOpen ? (
        <div className="auth-overlay">
          <div className="auth-modal sign-up-modal">
            <button
              className="auth-close"
              onClick={() => {
                setIsSignUpModalOpen(false);
                setSignUpError('');
              }}
              aria-label="Close sign up modal"
            >
              ×
            </button>

            <form className="auth-form signup-form">
              <label className="floating-field">
                <span>GIVEN NAMES *</span>
                <input type="text" value={signUpFirstName} onChange={(event) => setSignUpFirstName(event.target.value)} />
              </label>
              <label className="floating-field">
                <span>SURNAME *</span>
                <input type="text" value={signUpLastName} onChange={(event) => setSignUpLastName(event.target.value)} />
              </label>
              <label className="floating-field">
                <span>EMAIL *</span>
                <input type="email" value={signUpEmail} onChange={(event) => setSignUpEmail(event.target.value)} />
              </label>
              <label className="floating-field">
                <span>PASSWORD *</span>
                <input type="password" value={signUpPassword} onChange={(event) => setSignUpPassword(event.target.value)} />
              </label>
              <label className="floating-field">
                <span>CONFIRM PASSWORD *</span>
                <input type="password" value={signUpConfirmPassword} onChange={(event) => setSignUpConfirmPassword(event.target.value)} />
              </label>
              <label className="floating-field">
                <span>MOBILE *</span>
                <input type="tel" value={signUpPhone} onChange={(event) => setSignUpPhone(event.target.value)} />
              </label>

              <button type="button" className="auth-submit" disabled={isSigningUp} onClick={handleSignUp}>
                {isSigningUp ? 'Signing Up...' : 'SIGN UP'}
              </button>
              {signUpError ? <p className="auth-status-message auth-status-error">{signUpError}</p> : null}

              <p className="auth-switch">
                ALREADY A MEMBER?{' '}
                <button
                  type="button"
                  className="auth-link-btn inline"
                  onClick={() => {
                    setIsSignUpModalOpen(false);
                    setSignUpError('');
                    setSignInError('');
                    setIsSignInModalOpen(true);
                  }}
                >
                  SIGN IN
                </button>
              </p>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
